/**
 * The ability IR — the serializable data tree the combinator DSL emits and the engine
 * interprets (SPEC.md §5.1, CONTEXT.md "Ability IR").
 *
 * Everything in this module is **plain JSON data**: discriminated unions keyed by `kind`,
 * numbers, strings, booleans and arrays. No functions, no classes, no `Map`/`Set`. Three
 * properties follow from that and are what the rest of the design leans on:
 *
 * 1. **It round-trips through JSON losslessly**, so an IR snapshot is a reviewable artifact
 *    (SPEC.md §5.4: "review = IR snapshot diff + test") and a card's behaviour diffs as data.
 * 2. **Triggers, costs, selectors and choices stay inspectable**, so `legalActions` can
 *    enumerate a target pool without speculatively executing anything — the AI and the UI
 *    read the same list (SPEC.md §5.1).
 * 3. **It rides inside `GameState`** — a bag item and a half-resolved effect frame carry IR
 *    nodes — so it clones and serializes like the rest of the state.
 *
 * The IR is treated as **immutable** once built. `cloneIr` exists for the places that copy a
 * state defensively; nothing ever mutates a node in place.
 *
 * ## Shape at a glance
 *
 * ```
 * Ability   = keyword | triggered | activated | static
 * Effect    = a tree: control flow (seq/if/may/modal/target) + primitive verbs
 * Selector  = a predicate over cards, evaluated to a pool of instance ids
 * Amount    = a number, or a value computed at resolution ("for each", "equal to their {S}")
 * Condition = a predicate over the game state (and the triggering event, for guards)
 * ```
 *
 * ## Extension points left for later tickets
 *
 * | Node | Completed by |
 * |---|---|
 * | `ActivationCost` — a cost component that needs a *choice* to pay | 16–19, via `payments.ts` |
 * | `Effect.modify` / `StaticEffect.buff` durations & guards | **done** — 10 (`modifiers.ts`) |
 * | `Effect.deal-damage` vs `put-damage` interception | **done** — 11 (`replacements.ts`) |
 * | `Effect.custom` | 13 — `custom()` escape hatch |
 *
 * Ticket 11 added the replacement half: `ReplacementAbility` (an interceptor that rewrites a
 * pending event before it happens, CR §6.5), `EventTemplate` (what it becomes) and the fourth
 * damage verb, `move-damage`.
 */
import type { CardTypeName } from '../cards.ts';
import type { GameEventType } from '../events.ts';
import type { InstanceId, PlayerIndex, Zone, ZonePlacement } from '../ids.ts';
import type { TurnPhase } from '../state.ts';

// ---------------------------------------------------------------------------
// Keywords (CR §8)
// ---------------------------------------------------------------------------

/**
 * The 15 named keywords across all released sets (rules inventory §5). Shift's six variants
 * are spelled as their own names on cards but share `Shift`'s semantics, so the variant name
 * lives in `KeywordAbility.variant` rather than widening this union.
 */
export type KeywordName =
  | 'Alert'
  | 'Bodyguard'
  | 'Boost'
  | 'Challenger'
  | 'Evasive'
  | 'Reckless'
  | 'Resist'
  | 'Rush'
  | 'Shift'
  | 'Singer'
  | 'Sing Together'
  | 'Support'
  | 'Underdog'
  | 'Vanish'
  | 'Ward';

export const KEYWORD_NAMES = [
  'Alert',
  'Bodyguard',
  'Boost',
  'Challenger',
  'Evasive',
  'Reckless',
  'Resist',
  'Rush',
  'Shift',
  'Singer',
  'Sing Together',
  'Support',
  'Underdog',
  'Vanish',
  'Ward',
] as const satisfies readonly KeywordName[];

/**
 * Keywords whose "+N" instances stack (CR §8.1.2). Every other keyword is idempotent — a
 * second Support does nothing.
 */
export const STACKING_KEYWORDS: readonly KeywordName[] = ['Challenger', 'Resist'];

/** Keywords that print a numeric parameter. */
export const NUMERIC_KEYWORDS: readonly KeywordName[] = [
  'Boost',
  'Challenger',
  'Resist',
  'Shift',
  'Singer',
  'Sing Together',
];

// ---------------------------------------------------------------------------
// Ink colors (CR §3.1)
// ---------------------------------------------------------------------------

/**
 * The six printed ink colors. A dual-ink card (e.g. Rhino - Motivational Speaker, printed
 * Amber-Steel) carries two of these in `CardDefinition.inks`; `Selector.ink` matches a card
 * that carries *this* one among however many it prints — "your other Amber characters" is a
 * has-this-ink test, not an is-exactly-this-ink one, so a dual Amber-Steel card still counts.
 */
export type InkColor = 'Amber' | 'Amethyst' | 'Emerald' | 'Ruby' | 'Sapphire' | 'Steel';

export const INK_COLORS = [
  'Amber',
  'Amethyst',
  'Emerald',
  'Ruby',
  'Sapphire',
  'Steel',
] as const satisfies readonly InkColor[];

// ---------------------------------------------------------------------------
// References
// ---------------------------------------------------------------------------

/**
 * Which card an effect acts on, resolved **when the effect resolves**, not when it was
 * announced (CR §6.7.3).
 *
 * `target` names a binding produced by an enclosing `target` effect node — that is where a
 * player choice enters the IR, and it is why targeting is inspectable data rather than a
 * closure.
 */
export type CardRef =
  /** A specific card, fixed when the IR was built (rare — mostly for tests). */
  | { readonly kind: 'instance'; readonly instance: InstanceId }
  /** The card whose ability this is. */
  | { readonly kind: 'self' }
  /** The card the triggering event was about (the quester, the banished card, …). */
  | { readonly kind: 'subject' }
  /**
   * The card that **caused** the triggering event — the challenging character behind a
   * challenge banish, the card whose ability chose this one, the dealer of damage. `null`
   * for an event with no card behind it, in which case the effect does nothing (CR §1.2.3).
   */
  | { readonly kind: 'event-source' }
  /**
   * The defender of the triggering `challenge-declared` — "banish that character" on a
   * challenger-side trigger (ticket 138, Boo - Energetic Child's KID-TASTROPHE!). Names
   * nothing for any other event (CR §1.2.3). `challengedCardIs`'s guard-only `Condition` asks
   * the same question of a *filter*; this is its `CardRef` twin, for an effect that acts on it.
   */
  | { readonly kind: 'event-defender' }
  /**
   * The location a card is at (CR §4.3.6), or none. "Gain lore equal to that location's ◊"
   * reads `lore-of(location-of(self))` from a "moves to a location" trigger.
   */
  | { readonly kind: 'location-of'; readonly of: CardRef }
  /**
   * The card on top of a player's deck — "put the top card of your deck into your inkwell",
   * "reveal the top card of your deck". Names nothing when the deck is empty (CR §1.2.3).
   */
  | { readonly kind: 'top-of-deck'; readonly player: PlayerRef }
  /**
   * The card on the bottom of a player's deck — "draw a card from the bottom of your deck"
   * (Merlin - Envisioning the Future). Names nothing when the deck is empty (CR §1.2.3).
   */
  | { readonly kind: 'bottom-of-deck'; readonly player: PlayerRef }
  /** A card chosen for the `TargetSpec` with this id. */
  | { readonly kind: 'target'; readonly id: string }
  /**
   * A reference narrowed to just the cards satisfying a selector, evaluated when read — the
   * general primitive ticket 141 built so a multi-instance binding (`bound('under-cards')`,
   * but any `CardRef` works) can be intersected with a `Selector` before a mass effect
   * consumes it. "You may play those characters from your discard for free" (Sulley & Boo -
   * Scare Buddies) needs `bound('under-cards')` filtered to `{ zone: 'discard', types:
   * ['Character'] }` first — otherwise a mass `playForFreeNow` would free-play every card that
   * was under, non-characters included.
   */
  | { readonly kind: 'matching'; readonly of: CardRef; readonly is: Selector };

/** Ticket 07's name for the same thing. Kept so the bag's stub vocabulary still compiles. */
export type CardTarget = CardRef;

/** Which player an effect acts on. `opponent` reads the next player in turn order. */
export type PlayerRef =
  | { readonly kind: 'player'; readonly player: PlayerIndex }
  | { readonly kind: 'controller' }
  | { readonly kind: 'opponent' }
  /** "…**its player** draws a card" — whoever controls the card this names. */
  | { readonly kind: 'card-controller'; readonly of: CardRef }
  /** A player bound by an earlier `choose-player` node with this id — see `PlayerTargetSpec`. */
  | { readonly kind: 'chosen'; readonly id: string };

/** Ticket 07's name. */
export type PlayerTarget = PlayerRef;

/**
 * Which players are offered by a `choose-player` binding (ticket 102) — the player-scoped
 * counterpart of `Selector`'s `controller` filter, but as a *pool to pick from* rather than a
 * fixed relation. `any` is CR's plain "chosen player" (every player, including yourself);
 * `opponent` is "chosen opponent", narrowed the same way `Selector.controller: 'opponent'`
 * narrows a card pool. In the 2-player product (SPEC.md §4.6) `opponent` always has exactly one
 * candidate, so it auto-resolves without a decision (the same single-candidate rule `target`
 * follows) — the pool still exists as data so an N-player table asks a real question.
 */
export type PlayerPool = 'any' | 'opponent';

/**
 * One player a resolving effect picks (CR's "chosen player"/"chosen opponent" phrasing) — the
 * `TargetSpec` counterpart for players rather than cards (ticket 102). Bound with `{ kind:
 * 'chosen', id }`, exactly as a `TargetSpec` binds with `{ kind: 'target', id }`.
 */
export interface PlayerTargetSpec {
  /** Binding name; `PlayerRef { kind: 'chosen', id }` reads it back. Unique within an effect. */
  readonly id: string;
  /** Who is eligible. Default `any` — every player. */
  readonly pool?: PlayerPool;
  /** Who picks. Default the ability's controller. */
  readonly chooser?: 'controller' | 'opponent';
  /** Debug/UI text. */
  readonly label?: string;
}

// ---------------------------------------------------------------------------
// Selectors — the target pools `legalActions` enumerates
// ---------------------------------------------------------------------------

/**
 * A predicate over cards. Evaluating one against the state yields the ordered pool of
 * instance ids that satisfy it — which is exactly the option list of a targeting
 * `ChoiceRequest`, and exactly what the AI reads to enumerate its moves.
 *
 * Absent fields do not constrain. The ~20 predicates here cover essentially all of the
 * game's targeting per the ability-representation analysis (§1.3).
 */
export interface Selector {
  /**
   * "An item card **or** a Robot character card" — a disjunction, for the sentences whose two
   * halves constrain different fields and so cannot be one flat predicate.
   *
   * Each alternative is read **on top of** the enclosing selector, so the limiters they share
   * (the zone, the controller) are written once and only the difference goes in the branch.
   */
  readonly or?: readonly Selector[];
  /** Where to look. Default `play`. */
  readonly zone?: Zone;
  /** Relative to the ability's controller. Default `any`. */
  readonly controller?: 'you' | 'opponent' | 'any';
  /**
   * The controller is **whichever player a `choose-player` binding named** — "chosen player's
   * discard" (ticket 102) — where `controller`'s fixed you/opponent/any vocabulary cannot say
   * "whichever one was picked" because that is not known until the effect resolves. Takes
   * priority over `controller` when both are present; a card module never sets both.
   */
  readonly controllerRef?: PlayerRef;
  readonly types?: readonly CardTypeName[];
  /** A classification, e.g. `Princess`, `Broom` (CR §5.3.3.2). */
  readonly subtype?: string;
  /**
   * Any one of these classifications — "your Prince, Princess, King, and Queen characters".
   * A card carrying two of them still matches once, which is why this is not four selectors.
   */
  readonly anySubtype?: readonly string[];
  /**
   * Excludes cards carrying this classification — "an action that isn't a song" (CR §5.3.3.2).
   * Song is a subtype of Action, not a card type of its own, so this is what separates the two:
   * `{ types: ['Action'], withoutSubtype: 'Song' }` is a non-song action.
   */
  readonly withoutSubtype?: string;
  /** A card name, matched against `CardDefinition.names` (so ampersand cards match either). */
  readonly name?: string;
  /**
   * Excludes cards with this name — "your characters not named Mor'du". A literal matches a
   * card's base name(s) or its full name; a reference compares the referenced card's base
   * names, so `withoutName: self` means "not named this card" and follows reprints without
   * hardcoding the source's printed name.
   */
  readonly withoutName?: string | CardRef;
  /**
   * A printed ink color, matched against `CardDefinition.inks` — "your other Amber
   * characters" (CR §3.1). A dual-ink card matches on *either* of its two inks (has-this-ink,
   * not is-exactly-this-ink): Rhino - Motivational Speaker (Amber-Steel) is one of "your
   * Amber characters" and one of "your Steel characters" both.
   */
  readonly ink?: InkColor;
  /**
   * The card carries the printed inkwell symbol ◉ (CR §4.2) — "choose and discard a card with
   * ◉" (Stitch - Experiment 626's STEALTH MODE). Matched against `CardDefinition.inkwell`, the
   * same boolean `rules.ts` reads before letting a card be inked. Printed only: a hand that
   * "counts as having ◉" through a static (`handCountsAsInkable`) is a different question —
   * this field never reads that override, because the printed symbol is what the sentence
   * names.
   */
  readonly inkable?: boolean;
  readonly keyword?: KeywordName;
  readonly exerted?: boolean;
  readonly damaged?: boolean;
  /**
   * "Cost up to 2 more than the banished character" (Retro Evolution Device, ticket 111): a
   * literal number for the common case, or a computed `Amount` — typically a bound card's own
   * `cost-of` plus a constant — for a cap that is only known once an earlier cost or choice has
   * resolved. Evaluated fresh against the live state every time the selector is read, exactly
   * as any other `Amount` is (CR §6.1.8).
   */
  readonly costAtMost?: number | Amount;
  readonly costAtLeast?: number;
  readonly strengthAtMost?: number;
  readonly strengthAtLeast?: number;
  /**
   * `strengthAtMost`'s computed-threshold form — "…{S} equal to or less than [some value]"
   * where the value is not a literal number: Wreck-It Ralph - Raging Wrecker's WHO'S COMIN'
   * WITH ME banishes by the {S} he had in play, which only exists as a snapshot on the banish
   * event that set the ability off (`triggeringAmount()`, ticket 104).
   */
  readonly strengthAtMostAmount?: Amount;
  readonly willpowerAtMost?: number;
  readonly willpowerAtLeast?: number;
  /** Excludes cards *with* this keyword — "each opposing character without Evasive". */
  readonly withoutKeyword?: KeywordName;
  /** "another …" — excludes the ability's own source (CR §6.1.6). */
  readonly excludeSelf?: boolean;
  /** Excludes cards already bound to an earlier `TargetSpec` of the same effect. */
  readonly excludeChosen?: boolean;
  /**
   * **This card only** — the ability's own source. What "this character gets +1 ◊" needs when
   * it is a *continuous static* rather than an applied one, because a static's pool is a
   * selector rather than a reference (CR §6.4.2.1).
   */
  readonly self?: boolean;
  /** The card is at a location (CR §4.3.6) — "your characters at locations". */
  readonly atLocation?: boolean;
  /**
   * The card has at least one card **under** it — Boost's facedown pile and Shift's base
   * (CR §8.3, §8.10.4). "If there's a card under him" is the whole of the Boost family's
   * condition, and it is a selector rather than an amount because that is how it is written.
   */
  readonly hasCardsUnder?: boolean;
  /**
   * The card is **under** this specific card (CR §8.10.4, §8.3) — "characters under this
   * item" (The Black Cauldron). `hasCardsUnder` asks the question the other way around, off
   * the top card; this is the pool of the pile beneath it, which needs `zone: 'under'` on the
   * same selector to reach the `under` zone at all.
   */
  readonly under?: CardRef;
  /**
   * The pile this card is under is **topped by a card of one of these types** (ticket 103) —
   * "cards from under your characters and locations" (Visiting Christmas Past), which must
   * pass over a card under an *item* pile (The Black Cauldron) without a player choosing which
   * piles count. `under` filters by one named top card; this filters by the top's printed
   * type, needing `zone: 'under'` on the same selector exactly as `under` does. A card that is
   * not under anything never matches.
   */
  readonly underType?: readonly CardTypeName[];
  /**
   * The card is challenging the ability's source **right now** (CR §4.6) — "while this
   * character is being challenged, the challenging character gets -1 {S}". Read off the live
   * challenge frame, the same place Challenger's own window is read from, so it needs no
   * state field and no cleanup.
   */
  readonly challengingSource?: boolean;
  /**
   * The card is **here**: at the same location as the ability's source, or — when the source
   * *is* a location — at that location. This is the CR's "while here" scope (§5.6).
   */
  readonly here?: boolean;
  /**
   * The card is at the location this reference names (CR §4.3.6). `here` is the same question
   * asked about the ability's *own* source; this is the form a sentence needs when the location
   * was **chosen** — "move any number of your characters to the same location", which has to
   * be able to ask which of them are already standing there.
   */
  readonly at?: CardRef;
  /**
   * The card is one a player is currently **looking at** — the cards a resolving
   * "look at the top N cards of your deck" put in front of them (CR §7.9). They are still in
   * the deck; this is what lets the rest of the sentence choose among them.
   */
  readonly looking?: boolean;
  /**
   * The character has (or has not) quested this turn (CR §4.5) — "if no other character has
   * quested this turn". Reset in the Ready step, like every other per-turn count.
   */
  readonly questedThisTurn?: boolean;
  /**
   * The character was (or was not) the **defender** of a challenge declared this turn (CR
   * §4.6) — "chosen character who was challenged this turn". Reset in the Ready step, like
   * `questedThisTurn`.
   */
  readonly wasChallengedThisTurn?: boolean;
  /**
   * The card had a card put **under** it during the current turn (`GameState
   * .cardsPutUnderThisTurn`) — "if you've put a card under her this turn" (matched by
   * identity, unlike `hasCardsUnder`'s live pile check: the fact survives even a pile that is
   * later dissolved). Reset in the Ready step, like `questedThisTurn`.
   */
  readonly cardPutUnderThisTurn?: boolean;
  /**
   * Among the top N cards of the zone — "look at the top 4 cards of your deck". Positional, so
   * it is only meaningful in an ordered zone (a deck); `deck[0]` is the top.
   *
   * This is what keeps "choose one of the top 4" a **selector** rather than a list of ids a
   * `custom()` transcript would have to record: the pool is recomputed from the state, so the
   * only thing the transcript keeps is which card was picked (SPEC.md §4.4 — a deck's *order*
   * is never in a view).
   */
  readonly topOfDeck?: number;
  /**
   * The card may be **played** right now — nothing restricts playing it (CR §6.6.5), read in
   * whatever zone it is in. What a "play a character from your discard for free" pool needs so
   * a card whose own text forbids the play ("you can't play this character unless …") is never
   * offered as a choice; `playForFreeNow` adds it to the pool it is handed.
   */
  readonly playable?: boolean;
  /**
   * The card has at least one legal **single-card** Shift base in play right now. This is the
   * effect-facing form of Shift legality: "Shift a character from your discard for free"
   * must not offer a character that cannot actually land on a matching base. Duo Shift's
   * two-card payment remains a normal-play-only shape until an effect prints that wording.
   */
  readonly shiftable?: boolean;
  /**
   * This card is a legal single-card Shift base for the character the reference names.
   * Evaluated from `shiftBaseSets`, so same-name, classification, Universal, Potato and
   * counts-as-named rules stay in the cost model rather than being reimplemented by effects.
   */
  readonly shiftBaseFor?: CardRef;
  /**
   * The controller could **pay this card's cost** in ink right now (CR §4.3.3) — `playable`'s
   * money half, and what a "you may play a card from your discard" pool needs once the play
   * is not free: a card nobody can pay for is not an option (`playPayingNow` adds it to the
   * pool it is handed, as `playForFreeNow` adds `playable`).
   */
  readonly affordable?: boolean;
}

// ---------------------------------------------------------------------------
// Amounts — numbers computed at resolution time
// ---------------------------------------------------------------------------

/**
 * A quantity. A plain `number` for the common case; the computed forms are the "for each"
 * and "equal to their {S}" families, counted **once, at resolution** for non-static
 * abilities (CR §6.1.8).
 */
export type Amount =
  | number
  | { readonly kind: 'count'; readonly of: Selector }
  /**
   * "Draw a card for each different ink type of cards revealed this way" (Colors of the Wind,
   * ticket 105) — the number of *distinct* printed inks (`CardDefinition.inks`) among a pool,
   * not the number of matching cards. A dual-ink card contributes each of its printed inks
   * once; two cards sharing an ink still count that ink only once. Pair with `Selector.looking`
   * to count cards an earlier `look-at-deck`/reveal effect just surfaced (`looking()`).
   */
  | { readonly kind: 'distinct-ink-count'; readonly of: Selector }
  | { readonly kind: 'strength-of'; readonly of: CardRef }
  | { readonly kind: 'willpower-of'; readonly of: CardRef }
  | { readonly kind: 'lore-of'; readonly of: CardRef }
  /**
   * "The same cost … as [that card]" — a card's own ink cost, read as a value rather than
   * checked against a literal (`Selector.costAtMost`). Only meaningful inside `custom()`
   * today: a declarative `target` node can't offer a hand-zone pool to pick the "other" card
   * from (`selectors.ts`'s `targetPool` seam), so this is read via `readAmount` and folded
   * into a plain `costAtMost` number for the selector that follows.
   */
  | { readonly kind: 'cost-of'; readonly of: CardRef }
  | { readonly kind: 'damage-on'; readonly of: CardRef }
  /** "for each 1 damage on opposing characters" — the counters, summed over a pool. */
  | { readonly kind: 'damage-total'; readonly of: Selector }
  /** "for each card under this character" — the size of its pile (CR §8.3). */
  | { readonly kind: 'cards-under'; readonly of: CardRef }
  /**
   * How many cards a binding names — "for each character that sang this song". The binding is
   * usually one the *event* supplied (`singers`), which no selector could describe afterwards.
   */
  | { readonly kind: 'bound-count'; readonly id: string }
  /**
   * A player's lore total. What "you gain lore equal to the lore lost" measures: read it
   * either side of the loss and the difference is what was actually lost, which is not the
   * number the card names whenever a player had less lore than that (CR §6.6.3).
   */
  | { readonly kind: 'player-lore'; readonly player: PlayerRef }
  /**
   * How many challenges have been declared this turn (CR §4.6) — "if it's the second challenge
   * this turn". Counted per *challenge*, so one character challenging twice counts twice.
   */
  | { readonly kind: 'challenges-this-turn' }
  /**
   * How many cards have been put into a player's inkwell this turn (`Player.cardsInkedThisTurn`)
   * — "if it's the **second** card you've put into your inkwell this turn" is `amount-equals` of
   * this at `2`. **Every** way a card gets there counts: the CR §4.2 turn action and the effects
   * that put cards into an inkwell alike, which is what the printed sentence says. The counter
   * increments before `card-inked` is emitted, so a trigger guard reads the count *including*
   * the card that fired it.
   */
  | { readonly kind: 'cards-inked-this-turn'; readonly player: PlayerRef }
  /**
   * How many cards a player has drawn this turn (`Player.cardsDrawnThisTurn`) — "how many cards
   * you've drawn this turn" (Ink Amplifier, 2356). Counted at `drawCard`'s single chokepoint
   * (`zones.ts`), the same one `card-drawn` fires from, so every way a card reaches a hand by
   * drawing counts and a failed draw (empty deck) does not.
   */
  | { readonly kind: 'cards-drawn-this-turn'; readonly player: PlayerRef }
  /** How many cards have entered a player's discard this turn, by any cause. */
  | { readonly kind: 'cards-put-into-discard-this-turn'; readonly player: PlayerRef }
  /**
   * How many cards a player has **discarded** this turn (`Player.cardsDiscardedThisTurn`) —
   * CR §7.3's own verb, narrower than `cards-put-into-discard-this-turn`: a card discarded
   * from a hand counts, a character that leaves play for the discard by being banished does
   * not (`card-discarded` only fires at `zones.ts`'s hand→discard chokepoints).
   */
  | { readonly kind: 'cards-discarded-this-turn'; readonly player: PlayerRef }
  /**
   * Damage removed from characters during the current turn. With `player`, reads that seat's
   * characters; without it, sums every seat. Both removing and moving damage count, and only
   * the amount actually removed is accumulated.
   */
  | { readonly kind: 'damage-removed-this-turn'; readonly player?: PlayerRef }
  /**
   * How many cards matching this selector have been played this turn (`GameState.playedThisTurn`)
   * — "whenever you play a **second** action in a turn" is `amount-equals` of this at `2`. Matched
   * on printed characteristics and controller, the same as the `played-this-turn` condition,
   * because a played Action is usually already in the discard by the time anything asks.
   */
  | { readonly kind: 'played-this-turn-count'; readonly of: Selector }
  /**
   * How many cards matching this selector were banished in a challenge this turn
   * (`GameState.banishedInChallengeThisTurn`, reset in the Ready step) — the counting form of
   * the `banished-in-challenge-this-turn` condition, for the "for each opposing character
   * banished in a challenge this turn" family. Matched on printed characteristics and
   * controller, like `played-this-turn-count`: the banished card is in a discard by now.
   */
  | { readonly kind: 'banished-in-challenge-this-turn-count'; readonly of: Selector }
  /** The negation of another amount — what "loses ¤ equal to …" and "pay 1 less for each …" want. */
  | { readonly kind: 'minus'; readonly of: Amount }
  /**
   * A scaled amount — the "N per each" of "you pay **2** {I} less for each …", which `minus`
   * alone cannot say. `n` is the printed multiplier, `of` the count it scales.
   */
  | { readonly kind: 'times'; readonly n: number; readonly of: Amount }
  /**
   * The sum of two amounts — "cost up to **2 more than** the banished character" (Retro
   * Evolution Device, ticket 111) is `plus(theirCost(bound('cost-banished')), 2)`. Kept
   * general rather than a bespoke "plus a constant" node, since either side may itself be
   * computed.
   */
  | { readonly kind: 'plus'; readonly a: Amount; readonly b: Amount }
  /**
   * The numeric value carried by the event that triggered this ability: `amount` for damage
   * and ink-exertion events, or the ink actually paid (`cost`) for `card-played`. Available to
   * the trigger guard and later to its resolving effect (unlike `EventAmount`'s `event-amount`,
   * which only a replacement's own produced event can see). Anything else reads 0.
   */
  | { readonly kind: 'triggering-amount' }
  /**
   * A preceding effect step's actual numeric result, bound via `as` (`remove-damage`'s `as`
   * today) — resolution-local, unlike the `this-turn` counters, and `triggeringAmount`'s
   * sequential, effect-result counterpart: that reads what the *triggering event* carried,
   * this reads what an *earlier step of this same effect tree* actually produced. Unbound (or
   * never reached) reads 0, the ordinary "do as much as you can" (CR §1.2.3).
   */
  | { readonly kind: 'bound-amount'; readonly id: string }
  /**
   * The **printed move cost** of the card this ability is printed on (CR §4.3.6) — what a
   * location's own "your [mover] characters may move here for free" reduces, without the
   * module restating the number the card already prints. Read off the source card, so it
   * stays right through an errata and cancels whatever the printed cost is; 0 for a card
   * with no move cost.
   */
  | { readonly kind: 'own-move-cost' }
  /**
   * The lesser of two amounts — "gain lore equal to her {S}, **to a maximum of** 6 lore"
   * (Mulan - Resourceful Recruit). `max` is usually a literal ceiling but can itself be
   * dynamic, matching `minus`/`times`'s generality. Composes with any amount and any effect
   * that takes one (`gainLore`, `dealDamage`, …) rather than being its own verb, ticket 113.
   * Built by the DSL's `amountAtMost` (named apart from `atMost`, the `count-at-most`
   * Condition over a Selector).
   */
  | { readonly kind: 'amount-at-most'; readonly of: Amount; readonly max: Amount };

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

/**
 * A predicate over the game state, used both as a trigger guard ("while this character is
 * exerted, whenever …") and as an effect-level conditional ("if you have …").
 *
 * A guard is evaluated with the triggering event in scope, which is what the
 * `event-*` members read; used anywhere else they are simply false.
 */
export type Condition =
  | { readonly kind: 'always' }
  | { readonly kind: 'not'; readonly of: Condition }
  | { readonly kind: 'all'; readonly of: readonly Condition[] }
  | { readonly kind: 'any'; readonly of: readonly Condition[] }
  | { readonly kind: 'count-at-least'; readonly of: Selector; readonly n: number }
  | { readonly kind: 'count-at-most'; readonly of: Selector; readonly n: number }
  /** "if an opponent has more cards in their hand than you" — one pool bigger than another. */
  | { readonly kind: 'count-more-than'; readonly of: Selector; readonly than: Selector }
  /**
   * "an opponent with more lore than you" — one `Amount` bigger than another. `count-more-than`
   * asks the same thing of two card pools; this is the version for anything else an `Amount`
   * can read, chiefly `player-lore`.
   */
  | { readonly kind: 'amount-more-than'; readonly of: Amount; readonly than: Amount }
  /**
   * "You have a character in play with more ¤ than each opposing character" — a max-vs-max
   * comparison over **two pools** (Faline - Playful Fawn's PRECOCIOUS FRIEND, Flynn Rider -
   * Frenemy's NARROW ADVANTAGE), re-evaluated on every read so it can drive a `whileActive`
   * static rather than only `custom()`'s one-shot check. Neither `count-more-than` (pool
   * *sizes*) nor `amount-more-than` (two fixed `Amount`s) can say "the best of one pool beats
   * every member of another" — this is that shape, live. Passing a single-card selector
   * (`thisCard()`) for `of` reduces it to "this character's ¤ exceeds each opposing character".
   *
   * Vacuously true when `than` is empty and `of` is not — nothing to fail against, matching
   * `custom()`'s precedent for the same sentence. False when `of` is empty — nothing to satisfy
   * it.
   */
  | { readonly kind: 'strength-exceeds-each'; readonly of: Selector; readonly than: Selector }
  /** The named card satisfies the selector — "if chosen character is damaged". */
  | { readonly kind: 'card-matches'; readonly card: CardRef; readonly is: Selector }
  /**
   * At least one card a (possibly multi-instance) reference names satisfies the selector — "if
   * any of the cards that were under them are character cards" (Sulley & Boo - Scare Buddies,
   * ticket 141). `card-matches`'s "any of" form: that one reads a single card off `card` (its
   * first, for a multi-instance ref); this asks the question a mass binding like
   * `bound('under-cards')` actually needs.
   */
  | { readonly kind: 'any-card-matches'; readonly card: CardRef; readonly is: Selector }
  /** It is the ability controller's turn. */
  | { readonly kind: 'your-turn' }
  /**
   * It is the ability controller's **first** turn (CR §2.1.4, §8.13) — the turn-relative half
   * of Underdog's "if this is your first turn" (`isYourFirstTurn`, `state.ts`), generalised as
   * a plain `Condition` so a triggered guard or a `whileActive` gate can ask the same question
   * without going through the Underdog keyword. Ticket 107.
   */
  | { readonly kind: 'your-first-turn' }
  /**
   * The ability controller was the CR §2.1.3 starting/first player. Pair with `not()` for
   * "you're not the first player" — Underdog's other half (`isStartingPlayer`, `state.ts`).
   * Ticket 107.
   */
  | { readonly kind: 'starting-player' }
  /** The player named by the triggering event is an opponent of the controller. */
  | { readonly kind: 'event-player-is-opponent' }
  /** The card that caused the triggering event is of one of these types (Vanish: `Action`). */
  | { readonly kind: 'event-source-type'; readonly types: readonly CardTypeName[] }
  /**
   * The triggering banish or damage happened **in a challenge** (CR §4.6) — "when this
   * character is banished in a challenge" (`card-banished`) or "deals damage … in a
   * challenge" (`damage-dealt`). `role` narrows it to the side the event's card was on, which
   * is the difference between "is challenged and banished" and "banishes another character".
   */
  | { readonly kind: 'event-in-challenge'; readonly role?: ChallengeRole }
  /** The card that caused the triggering event is the one this reference names. */
  | { readonly kind: 'event-source-is'; readonly card: CardRef }
  /**
   * The ability that caused the triggering event has this printed name — "whenever one of
   * your characters is chosen for **Support**", which has to tell Support's choice apart from
   * every other ability that chooses a card.
   */
  | { readonly kind: 'event-ability-is'; readonly name: string }
  /**
   * The triggering event happened **at the location this reference names** — "whenever a
   * character is banished **while here**". Read off the event, because the relationship is
   * already gone by the time the trigger is collected.
   */
  | { readonly kind: 'event-location-is'; readonly card: CardRef }
  /**
   * Guard-only: the moved character **came from a location** — the `from` slot of
   * `character-moved` (CR §4.3.6), which is what tells "move a character here **from a
   * location**" apart from a character that arrived here without ever having been elsewhere.
   * `event-location-is` asks about the location the mover went *to*; this asks about the one
   * it left, which no selector could name once the move is over.
   *
   * `is` narrows *which* location it left ("from a location you control"); absent, any prior
   * location meets it and a `from` of `null` never does.
   */
  | { readonly kind: 'event-moved-from'; readonly is?: Selector }
  /**
   * Guard-only: the zone a `card-returned` event's card left (ticket 76) — "from your discard"
   * is `zone: 'discard'`, the "from play" a bounce always was is `zone: 'play'`. Unlike
   * `event-moved-from` (a location, matched by selector) the origin here is one of the fixed
   * `Zone` literals, so it is compared directly.
   */
  | { readonly kind: 'event-returned-from'; readonly zone: Zone }
  /**
   * Guard-only: which side of a challenge the event's card was on — "when this character is
   * **challenged** and banished" is `defender`. Reads the `role` slot a
   * `banished-in-challenge` event carries; `event-in-challenge` is the same question asked of
   * a plain `card-banished`.
   */
  | { readonly kind: 'event-role'; readonly role: ChallengeRole }
  /**
   * Guard-only: the event's card had a card under it (Boost's facedown pile or a Shift base,
   * CR §8.3, §8.10.4) **just before** it left play — "if it had a card under it" (Merlin -
   * Completing His Research's LEGACY OF LEARNING). Reads the `hadCardsUnder` slot a
   * `card-banished`/`banished-in-challenge` event carries, snapshotted by `zones.ts`'s `banish`
   * before `moveInstance` dissolves the pile; the `hasCardsUnder` **selector** field asks the
   * same question of the board *now*, which by trigger-resolution time is always false for a
   * banished card — its pile is already gone.
   */
  | { readonly kind: 'event-had-cards-under' }
  /**
   * Guard-only: an exerted character stood at the event's banished location just before it
   * left play — "if there was an exerted character here" (The Frozen Vine - Monstrous Plant's
   * PERSISTENT PROBLEM, ticket 104). Reads `card-banished`'s `hadExertedCharacterHere` slot,
   * snapshotted the same way `hadCardsUnder` is: before `moveInstance` clears every occupant's
   * `location`.
   */
  | { readonly kind: 'event-had-exerted-character-here' }
  /**
   * Guard-only: the *other* card the event names (`by`) is the ability's own source — "whenever
   * this character banishes another character in a challenge".
   */
  | { readonly kind: 'event-other-is-self' }
  /**
   * Guard-only: the *other* card the event names matches this selector — "whenever **one of
   * your characters** banishes another character in a challenge". The same slot
   * `event-other-is-self` reads, asked of a pool rather than of one card.
   */
  | { readonly kind: 'event-other-is'; readonly is: Selector }
  /**
   * An amount is exactly this number — "if it's the **second** challenge this turn". The
   * count-based conditions above are thresholds over a *selector*; this is equality over any
   * `Amount`, which is what an ordinal ("the second") needs.
   */
  | { readonly kind: 'amount-equals'; readonly of: Amount; readonly n: number }
  /**
   * "While challenging a damaged character" — the ability's own card is the challenger of a
   * live challenge (CR §4.6) and the card it is challenging matches. Read off the resolution
   * stack, so it needs no state field and no cleanup.
   */
  | { readonly kind: 'challenging'; readonly is: Selector }
  /**
   * Guard-only: the **defender** of the triggering `challenge-declared` event matches — the
   * "whenever … challenges a *damaged* character" family, whose `filter` slot is already spent
   * on the challenger.
   */
  | { readonly kind: 'event-defender'; readonly is: Selector }
  /**
   * Guard-only: the card that banished the event's card in a challenge matches — "when this
   * character is banished by a Villain character". The banisher is `card-banished`'s `source`
   * slot, so this is `event-source-is` asked of a *pool* rather than of one reference.
   */
  | { readonly kind: 'event-banisher'; readonly is: Selector }
  /** "If 2 or more characters sang this song" — how many cards a binding names. */
  | { readonly kind: 'bound-at-least'; readonly id: string; readonly n: number }
  /**
   * "If you've played a song this turn" — a card matching this selector was played during the
   * current turn (`GameState.playedThisTurn`). Matched on printed characteristics and
   * controller only: by the time anything asks, an Action is already in the discard.
   */
  | { readonly kind: 'played-this-turn'; readonly of: Selector }
  /**
   * "If an opposing character was banished in a challenge this turn" — a card matching this
   * selector was banished in a challenge during the current turn
   * (`GameState.banishedInChallengeThisTurn`, reset in the Ready step). Matched on printed
   * characteristics and controller only, like `played-this-turn`: by the time anything asks,
   * the banished card is in a discard.
   */
  | { readonly kind: 'banished-in-challenge-this-turn'; readonly of: Selector }
  /**
   * "If a character was banished this turn" — a card matching this selector was banished
   * **by any cause** during the current turn (`GameState.banishedThisTurn`, reset in the Ready
   * step) — challenge, ability, lethal non-challenge damage, cost payment alike. A superset of
   * `banished-in-challenge-this-turn`. Matched on printed characteristics and controller only,
   * like `played-this-turn`: by the time anything asks, the banished card is in a discard.
   */
  | { readonly kind: 'banished-this-turn'; readonly of: Selector }
  /**
   * "…unless one of their characters has challenged this turn" — a card matching this selector
   * was the **challenger** of a challenge declared during the current turn
   * (`GameState.challengedThisTurn`, reset in the Ready step). Matched on printed
   * characteristics and controller only, like `played-this-turn`: a challenger banished in its
   * own challenge is in a discard by the time anything asks, and it challenged all the same.
   */
  | { readonly kind: 'challenged-this-turn'; readonly of: Selector }
  /**
   * "If 1 or more cards have left a player's discard this turn" (The Horned King - Triumphant
   * Ghoul) — a card matching this selector left **any player's** discard pile during the
   * current turn (`GameState.leftDiscardThisTurn`, reset in the Ready step). Matched on printed
   * characteristics and controller only, like `played-this-turn`; the printed sentence names no
   * restriction, so the card authors it with the default selector (any card, any controller).
   */
  | { readonly kind: 'left-discard-this-turn'; readonly of: Selector }
  /**
   * "If one of your characters took damage this turn" (Devil's Eye Diamond's THE PRICE OF
   * POWER) / "if an opposing character took damage this turn" (John Silver - Vengeful Pirate's
   * DRAWN TO A FIGHT) — a card matching this selector took damage during the current turn
   * (`GameState.damagedThisTurn`, reset in the Ready step), **any** character or location
   * matching the selector, not one bound reference — closer to a pool read than the other
   * this-turn conditions' single-card shape. Both `deal` and `put` verbs count, `move` does
   * not (`damage.ts`'s `performDamage`). Matched on printed characteristics and controller
   * only, like `played-this-turn`; unlike a banish fact, the card is usually still in play, but
   * the same matcher works regardless of zone.
   */
  | { readonly kind: 'damaged-this-turn'; readonly of: Selector }
  /**
   * "You may pay 1 {I} to …" — whether a player could pay that much right now (CR §4.3.2).
   * What makes the offer legal to put in front of them at all.
   *
   * `player` is whose ink is counted; absent it is the ability's controller. Naming somebody
   * else is what "…unless **their player** pays 3 {I}" needs, so the offer is skipped — and
   * the consequence applied — when the player being asked could never have paid.
   *
   * The amount is an `Amount` rather than a literal so an offer can be gated on a *card's*
   * cost — "you may play her" from a discard is `can-pay-ink` of `cost-of(self)`, which no
   * hand-written number could say without restating a printed statistic.
   */
  | { readonly kind: 'can-pay-ink'; readonly amount: Amount; readonly player?: PlayerRef };

/** Which side of a challenge a card was on (CR §4.6). */
export type ChallengeRole = 'challenger' | 'defender';

// ---------------------------------------------------------------------------
// The damage taxonomy (CR §1.9, rules inventory §10.8)
// ---------------------------------------------------------------------------

/**
 * The four damage verbs. They are **different pipelines**, not synonyms, and the difference
 * is observable:
 *
 * - **`deal`** — challenge damage and "deal N damage". The only verb damage modifiers see,
 *   so Resist +N reduces it (CR §8.9) — and, per the CR 2.2 clarification, that reduction
 *   happens *before* replacement effects get a look.
 * - **`put`** — "put N damage counters on". Bypasses modifiers entirely; Resist does nothing.
 * - **`remove`** — "remove N damage counters from".
 * - **`move`** — "move N damage counters from A to B": remove + put, but *neither* dealing
 *   nor putting for rules purposes. It emits no `damage-dealt` event, so nothing that
 *   triggers on damage being dealt fires; it does emit `damage-removed`, because moving
 *   counts as removing for triggers (official *Wilds Unknown* Q&A).
 */
export type DamageVerb = 'deal' | 'put' | 'remove' | 'move';

export const DAMAGE_VERBS = ['deal', 'put', 'remove', 'move'] as const satisfies readonly DamageVerb[];

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

export type Stat = 'strength' | 'willpower' | 'lore';

/**
 * A permission a continuous effect can take away (CR §6.6.5: a "can't" always wins over a
 * "can"). These are *legality* predicates, read by `rules.ts`, `characteristics.ts` and
 * `payments.ts` rather than summed like a stat, so each member has to be wired to the rule it
 * forbids — adding one means teaching one more predicate, never a new mechanism:
 *
 * | Member | Read by |
 * |---|---|
 * | `quest` | `canQuest` — "they can't quest for the rest of this turn" |
 * | `challenge` | `canChallenge` |
 * | `ready` | `canReady`, the Ready step — "can't ready at the start of their next turn" |
 * | `sing` | `payments.ts` — "this character can't ⟳ to sing songs"; with a `by` selector, "…can't sing songs without Sing Together" (ticket 139) |
 * | `play` | `payments.ts` — "opponents can't play actions" (the card played is the subject) |
 * | `enter-play-ready` | `playCard` — "this character enters play exerted" |
 * | `be-challenged` | `legalChallengeTargets` — "damaged characters can't challenge this one" |
 * | `move` | `legalMoveOptions`, `moveCharacter` — "this character can't move to locations" |
 * | `must-quest` | `legalTurnActions` — no end-turn while it can still quest |
 *
 * `must-quest` is the odd one out, and deliberately here rather than in a compulsion
 * vocabulary of its own: "must quest if able during their next turn" takes away exactly one
 * permission — the permission to **end the turn** — which is how Reckless's "must challenge"
 * is already modelled (CR §8.7).
 *
 * Both flavours of continuous effect produce one: the applied snapshot (`restrict`, a modifier
 * record) and the continuous static (`StaticEffect.restrict`, matched live). One reader,
 * `isRestricted`, consults both.
 */
export type Restriction =
  | 'quest'
  | 'challenge'
  | 'ready'
  | 'sing'
  | 'play'
  | 'enter-play-ready'
  /** "Damaged characters can't challenge this character" — the `by` selector says which. */
  | 'be-challenged'
  /** "This character can't move to locations" (CR §4.3.6). */
  | 'move'
  /** "…must quest if able during their next turn" — the turn stays open, as Reckless's does. */
  | 'must-quest';

export const RESTRICTIONS = [
  'quest',
  'challenge',
  'ready',
  'sing',
  'play',
  'enter-play-ready',
  'be-challenged',
  'move',
  'must-quest',
] as const satisfies readonly Restriction[];

/**
 * A permission a continuous effect can **give** (CR §6.6.5) — the mirror of `Restriction`.
 *
 * `challenge-ready` is spelled out in full on ~19 cards ("they may also challenge ready
 * characters this turn"). It is deliberately **not** Alert: Alert ignores the *Evasive*
 * limiter (CR §8.1), while this ignores the "must be exerted" one (CR §4.6.2), and a card can
 * have either without the other.
 *
 * `quest-while-drying` is Rush's quest-side counterpart: it lifts only the drying limiter
 * for questing. It does not make an exerted character ready, overcome Reckless or a `quest`
 * restriction, or grant the general permission to pay an {E} cost while drying.
 *
 * The `StaticEffect.allow` node's `condition` narrows which targets a given grant of this
 * permission covers ("…can challenge ready **damaged** characters", Gizmoduck - Suited Up) — a
 * modifier on the grant, not a second `Permission` value, since the permission being given is
 * still `challenge-ready` either way.
 */
export type Permission = 'challenge-ready' | 'quest-while-drying';

export const PERMISSIONS = [
  'challenge-ready',
  'quest-while-drying',
] as const satisfies readonly Permission[];

/**
 * A permission a continuous effect can give a **player** rather than a card — the player-scoped
 * mirror of `Permission` (CR §6.6.5).
 *
 * `extra-ink` is "This turn, you may ink an additional card": it raises the CR §4.2 per-turn
 * ink limit exactly as a `StaticEffect.extra-ink` does, but it belongs to the *player* for a
 * duration rather than to a card in play. That is the whole reason it exists: the card that
 * says it is an Action, which is in the discard before the turn it names is over, so there is
 * no card left for `inkLimitOf`'s `player.play` scan to find.
 */
export type PlayerPermission = 'extra-ink';

export const PLAYER_PERMISSIONS = ['extra-ink'] as const satisfies readonly PlayerPermission[];

/**
 * How long a modifier lives — the CR §6.1.13 duration vocabulary (SPEC.md §4.5).
 *
 * A duration is a *boundary*, not a countdown: `modifiers.ts` drops the record when the
 * boundary is crossed, and until then every characteristic read sees it. The other half of
 * §6.1.13 — "while …" — is not a duration at all but a `guard` on the record, re-evaluated on
 * every read, so a condition that flips back on brings the modifier back with it.
 */
export type Duration =
  /** Ends in the End-of-Turn phase of the turn it was created in (CR §3.4). */
  | 'this-turn'
  /** Ends as its controller's next turn begins, before the Ready step (CR §3.2). */
  | 'until-start-of-your-next-turn'
  /**
   * "During their next turn" — the affected card's controller's next turn, ending as it does.
   * Every effect written this way ("can't quest during their next turn", "gains Reckless during
   * their next turn") only *matters* on that turn, so one boundary is enough: the record
   * survives the turn it was made on and expires at the end of the following one.
   */
  | 'until-end-of-their-next-turn'
  /** Never expires; it ends only when the card it applies to leaves play (CR §1.9). */
  | 'permanent';

/** One target a player picks while an effect resolves (CR §6.7.3). */
export interface TargetSpec {
  /** Binding name; `CardRef { kind: 'target', id }` reads it back. Unique within an effect. */
  readonly id: string;
  readonly selector: Selector;
  /**
   * How many cards to pick. Default 1. Almost always a literal; the computed forms are what
   * "put the rest of what you are looking at on the bottom, in any order" needs — the size of
   * the remainder is not known until the ability resolves. Counted once, when this node opens
   * (CR §6.1.8), like every other `Amount`; it does not shrink as the multi-pick session's own
   * picks remove cards from the pool it was read from.
   */
  readonly count?: Amount;
  /** "up to N" — fewer (including none) is legal (CR §6.1.3). */
  readonly upTo?: boolean;
  /** "Chosen characters with total {S} N or less" — a cumulative cap across this binding. */
  readonly maxTotalStrength?: number;
  /** Debug/UI text. */
  readonly label?: string;
  /**
   * Who picks. Default the ability's controller; `opponent` is the "each opponent chooses and
   * banishes one of their characters" shape, where the **pool** is still read relative to the
   * ability's controller but the decision belongs to somebody else.
   *
   * Load-bearing for two things beyond whose turn it is to answer: Ward asks about the
   * *chooser* (CR §8.15), and `playerView` redacts a request against the player it is asked
   * of — so a chooser'd request is only ever offered to, and only ever legible to, them.
   */
  readonly chooser?: 'controller' | 'opponent';
}

/**
 * "Remove up to N damage **total** from any number of your characters" (ticket 135, I'm Never
 * Not by Your Side) — a shared numeric budget spent across a decision maker's own choice of
 * any number of `selector` candidates, with the split itself a player decision.
 *
 * Distinct from `TargetSpec.maxTotalStrength`, which caps *who may be chosen* by a summed
 * characteristic; this caps *how much of a shared pool* the chosen set may receive in total,
 * one candidate at a time, until the pool is spent, the player stops, or nobody eligible is
 * left (CR §1.2.3 "do as much as you can", CR §6.1.3 "up to N").
 */
export interface DistributeSpec {
  /** Binding name — reserved for a future effect that needs to read who was chosen. */
  readonly id: string;
  /** The pool of eligible characters; only ones currently carrying damage are ever offered. */
  readonly selector: Selector;
  /** The total budget to allocate, evaluated once when the node opens. */
  readonly pool: Amount;
  /** Debug/UI text. */
  readonly label?: string;
}

/**
 * One node of an effect tree. The control-flow nodes at the top are what a JSON schema
 * would have had to grow an expression language for (see the ability-representation
 * analysis, candidate B); here they are four node kinds the interpreter walks.
 *
 * The primitive verbs below them are the ~35-verb vocabulary that matches 91% of the game's
 * effect clauses.
 */
export type Effect =
  // -- control flow ---------------------------------------------------------
  /** Resolves without doing anything — the "may, declined" and placeholder case. */
  | { readonly kind: 'noop' }
  | { readonly kind: 'seq'; readonly steps: readonly Effect[] }
  /** Choose cards, then run `then` with the binding in scope. */
  | { readonly kind: 'target'; readonly spec: TargetSpec; readonly then: Effect }
  /**
   * "Chosen player"/"chosen opponent" (ticket 102) — the `target` node's player-scoped
   * counterpart. Binds a `PlayerIndex` under `spec.id`, readable back as `PlayerRef { kind:
   * 'chosen', id }` anywhere in `then`, including inside a `Selector.controllerRef` naming a
   * zone that belongs to whoever was picked ("chosen player's discard").
   */
  | { readonly kind: 'choose-player'; readonly spec: PlayerTargetSpec; readonly then: Effect }
  /**
   * "Each player may draw a card" (Donald Duck - Perfect Gentleman, ticket 125) — `choose-player`
   * asks about ONE player somebody picks; this asks about EVERY player, one at a time, with no
   * decision about *who* — only what happens to each. Runs `then` once per player in turn order
   * starting with the active player (CR's "in turn order" convention for a multi-player effect,
   * `turnOrderFrom`'s existing use in the bag), each with `PlayerRef { kind: 'chosen', id }`
   * bound to that iteration's player — the same binding shape `choose-player` reads back with,
   * so "each player **may** draw a card" is just `mayPlayer(who, …)` under this node, and every
   * other `PlayerRef`/`Selector.controllerRef` consumer needs no changes to read it. Each
   * player's own decisions (the nested `may`) are asked and fully resolved before the next
   * player's copy of `then` begins — turn order, not simultaneity (the printed clarification:
   * "each player individually decides").
   */
  | { readonly kind: 'each-player'; readonly id: string; readonly then: Effect }
  /**
   * Interpreter-only step `each-player` expands into, right before each player's copy of
   * `then` — never constructed by a card module directly. Binds `player` under `id` with no
   * decision and no `awaiting` (unlike `choose-player`'s bind, which follows a choice); reusing
   * the id across players is exactly the "shadow" pitfall a card module must avoid, but is safe
   * and intentional here because `resolvePlayerRef`'s `chosen` case reads the *last* binding for
   * an id and each player's `then` fully unwinds before the next player's bind runs.
   */
  | { readonly kind: 'bind-each-player'; readonly id: string; readonly player: PlayerIndex }
  /**
   * "You may …" — a yes/no asked at resolution (CR §6.1.4).
   *
   * `chooser` is who is asked. Absent — the overwhelmingly common case — it is the ability's
   * own controller. Naming somebody else is what "…**unless their player** pays 3 {I}" needs:
   * the offer belongs to the *challenging character's* controller, not to the card that
   * printed the sentence, and `card-controller` of the event's source is how the IR says so
   * without the interpreter having to know anything about challenges. Load-bearing beyond
   * whose turn it is to answer: `playerView` redacts a request against the player it is asked
   * of, so a chooser'd offer is only ever put to, and only ever legible to, them — the same
   * rule `TargetSpec.chooser` already follows.
   *
   * `otherwise` is the printed "unless": what happens when the offer is **declined**. A plain
   * `may` simply skips the rest of the sentence, which is right for "you may draw a card"; a
   * pay-or-else sentence has a consequence, and it is data on the node rather than a second
   * ability so that both branches read out of one place.
   */
  | {
      readonly kind: 'may';
      readonly then: Effect;
      readonly label?: string;
      readonly chooser?: PlayerRef;
      readonly otherwise?: Effect;
    }
  /** "Choose one —" (CR §6.1.3): the controller picks exactly one mode. */
  | {
      readonly kind: 'modal';
      readonly modes: readonly { readonly label: string; readonly effect: Effect }[];
    }
  | {
      readonly kind: 'if';
      readonly condition: Condition;
      readonly then: Effect;
      readonly otherwise?: Effect;
    }
  // -- lore and cards -------------------------------------------------------
  | { readonly kind: 'gain-lore'; readonly player: PlayerRef; readonly amount: Amount }
  | { readonly kind: 'lose-lore'; readonly player: PlayerRef; readonly amount: Amount }
  | { readonly kind: 'draw'; readonly player: PlayerRef; readonly count: Amount }
  // -- damage: CR §1.9's four verbs, one node each (see `DamageVerb`) --------
  /**
   * "Deal N damage" — reduced by Resist (CR §8.9).
   *
   * `target` names cards through the ordinary reference machinery; `scope` is the "each …"
   * form ("deal 2 damage to each opposing character"), whose pool is evaluated as the effect
   * resolves. Exactly one of the two is set — the builders see to that.
   */
  | {
      readonly kind: 'deal-damage';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly amount: Amount;
      /**
       * "This damage can't be reduced by Resist" (Piercing Attack, ticket 121) — still a
       * `deal`, so `damage-dealt` triggers and the `damagedThisTurn` fact fire exactly as any
       * other dealt damage; only `applyDamageModifiers`'s Resist step (`damage.ts`) is skipped.
       */
      readonly ignoresResist?: boolean;
    }
  /** "Put N damage counters on" — bypasses damage modifiers. */
  | {
      readonly kind: 'put-damage';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly amount: Amount;
    }
  | {
      readonly kind: 'remove-damage';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly amount: Amount;
      /**
       * Bind the actual damage removed — after clamping to what was there and any
       * replacements — for a later effect in the same tree to read via
       * `{ kind: 'bound-amount', id }` (Ohana Means Family, Reuben - Sandwich Expert).
       */
      readonly as?: string;
    }
  /**
   * "Remove up to N damage total from any number of your characters" (ticket 135) —
   * `remove-damage`'s shared-budget sibling. Where `remove-damage` applies one `amount` to
   * every pooled target, this raises a `distribute` decision: pick a candidate, pick how much
   * of the remaining pool to remove from it (removed immediately, through the same
   * `removeDamage` chokepoint), and repeat until the pool or the candidates run out or the
   * player stops.
   */
  | {
      readonly kind: 'distribute-remove-damage';
      readonly spec: DistributeSpec;
      /**
       * Bind the actual total removed across every pick — see `remove-damage`'s `as` (ticket
       * 100). Summed the same way: `damage-removed` events emitted while the node runs, not
       * the requested per-pick amounts.
       */
      readonly as?: string;
    }
  /**
   * "Move N damage counters from A to B" — neither dealing nor putting, so no damage
   * modifier applies and no "dealt damage" trigger sees it (CR §1.9, rules inventory §10.8).
   *
   * `from` names a single source, the ordinary reference way; `fromScope` is the "move 1
   * damage from each other character" iteration (Morgana Macawber - Reformed Spellcaster,
   * ticket 108) — its pool is evaluated once, as `deal-damage`'s `scope` is, and each pooled
   * source then moves `amount` to the one bound `to`. Exactly one of the two is set — the
   * builders see to that.
   */
  | {
      readonly kind: 'move-damage';
      readonly from?: CardRef;
      readonly fromScope?: Selector;
      readonly to: CardRef;
      readonly amount: Amount;
    }
  // -- board ----------------------------------------------------------------
  /**
   * The board verbs. Each takes either a `target` reference or the "each/all …" `scope`,
   * evaluated once as the effect resolves — "ready your other Princess characters".
   */
  | { readonly kind: 'banish'; readonly target?: CardRef; readonly scope?: Selector }
  | { readonly kind: 'exert'; readonly target?: CardRef; readonly scope?: Selector }
  | { readonly kind: 'ready'; readonly target?: CardRef; readonly scope?: Selector }
  | { readonly kind: 'return-to-hand'; readonly target?: CardRef; readonly scope?: Selector }
  /**
   * "Put the top card of your deck facedown under this card" — Boost's effect (CR §8.3), and
   * the general put-under verb. The card is not played and not in play afterwards; the pile
   * it joins is `stacks.ts`. An empty deck does nothing (CR §1.2.3).
   *
   * `scope` is the "each …" form — "put a card from the top of your deck facedown under each
   * of your other characters" (Scrooge McDuck - Reformed Ebenezer's SPREADING JOY) — evaluated
   * once, as `deal-damage`'s `scope` is, into a snapshotted, ordered group; one deck card moves
   * under each member in that order until the deck is empty, so an undersized deck leaves the
   * rest untouched rather than failing the whole effect (CR §1.2.3), and a member that has left
   * play by its turn in the order is simply skipped. Exactly one of `target`/`scope` is set.
   *
   * `as`, only meaningful with `scope`, binds the members that actually **received** a card —
   * not the whole snapshotted group — under this id, so a coupled follow-up effect ("those
   * characters gain Ward until the start of your next turn") reaches only the successful
   * recipients (`CardRef { kind: 'target', id }` reads the binding back).
   */
  | {
      readonly kind: 'put-under-from-deck';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly as?: string;
    }
  /**
   * "Put a character card from your discard under this item faceup" (The Black Cauldron) —
   * the general put-under verb for a **named** card from wherever it is, rather than the top
   * of a deck, and **faceup**: its identity stays known to whoever already knew it (CR
   * §8.10.4). `target` is the card that moves; `under` is the card whose pile it joins, which
   * must be in play. A `target` that has left the zone it was chosen from, or an `under` that
   * has left play, does nothing (CR §1.2.3).
   */
  | { readonly kind: 'put-card-under'; readonly target: CardRef; readonly under: CardRef }
  // -- looking at, and rearranging, a deck (CR §7.9, §7.2) -------------------
  /**
   * "Look at the top N cards of your deck" — and, with `reveal`, "reveal" them, which is the
   * same act done publicly. The cards **stay in the deck**; what changes is who knows what
   * they are, and that they are now nameable by the rest of the sentence (`Selector.looking`).
   *
   * The set is cleared when the ability finishes, so no ability can leave a player looking.
   */
  | {
      readonly kind: 'look-at-deck';
      readonly player?: PlayerRef;
      readonly count: Amount;
      /** Publicly (CR §7.9) rather than privately. Default private. */
      readonly reveal?: boolean;
    }
  /** "…and put it into your hand." A card returns to its **owner**'s hand (CR §7.1.3). */
  | { readonly kind: 'put-into-hand'; readonly target: CardRef }
  /** "Put the rest on the bottom of your deck", "put it on the top of your deck". */
  | {
      readonly kind: 'put-on-deck';
      readonly target: CardRef;
      readonly placement: 'top' | 'bottom';
    }
  /** Stop looking early. The ability's end does this anyway; a card rarely needs it. */
  | { readonly kind: 'stop-looking'; readonly player?: PlayerRef }
  /** "Put [card] into your inkwell facedown and exerted" (CR §4.2 by an effect, not the action). */
  | {
      readonly kind: 'put-into-inkwell';
      readonly target: CardRef;
      /** Whose inkwell. Default the ability's controller — "put that card into **your**". */
      readonly player?: PlayerRef;
      /** "…and exerted", which is the usual wording. Default `true`. */
      readonly exerted?: boolean;
      /**
       * "…facedown": knowledge is **not** granted. A card put into an inkwell from a hidden
       * zone stays unknown to everybody, which is the Mickey Mouse - Detective clarification.
       */
      readonly facedown?: boolean;
    }
  /**
   * "Each opponent chooses and discards a card" — the **named player** chooses, not the
   * ability's controller, and their hand is hidden, so the choice is its own request keyed by
   * printed id (SPEC.md §4.4).
   */
  | {
      readonly kind: 'discard-chosen';
      readonly player: PlayerRef;
      readonly count: Amount;
    }
  /**
   * "…at random" — the random twin of `move-card`: relocate N cards from one of the named
   * player's zones, each picked by the engine's own seeded RNG (SPEC.md §4.2), exactly as the
   * deck shuffle draws from `state.rng`, so a replay from the same seed moves the identical
   * cards. Nobody chooses, so nothing is asked. "Discards a card at random" is
   * `from: 'hand', to: 'discard'`; "return 2 cards at random from your inkwell to your hand"
   * is `from: 'inkwell', to: 'hand'`. Too small a source zone moves what is there — as much
   * as possible — and an empty one moves nothing (CR §1.2.3).
   */
  | {
      readonly kind: 'move-random';
      readonly player: PlayerRef;
      /** The zone the picks draw from — re-read after every pick, so no card moves twice. */
      readonly from: Zone;
      readonly count: Amount;
      readonly to: Zone;
      /** Decks are ordered: `bottom` is "on the bottom of your deck". Default `top`. */
      readonly placement?: ZonePlacement;
      /** "…and exerted". Only meaningful in play or the inkwell. */
      readonly exerted?: boolean;
      /** "…facedown": knowledge is **not** granted. */
      readonly facedown?: boolean;
      /** Shuffle the destination deck afterwards. */
      readonly shuffle?: boolean;
    }
  /**
   * Move a card to another zone — the general zone verb behind "discard a card", "shuffle it
   * into its player's deck" and the rest of the relocations that have no named rules action of
   * their own.
   *
   * Unlike every other board verb this one does **not** require the card to be in play: its
   * whole point is the cards that are not. `banish` / `return-to-hand` stay separate because
   * they are the *named* rules actions, with their own events and their own triggers; this is
   * the plain relocation underneath them, and the deck/hand/inkwell verbs above are the spelt
   * out forms that carry an event or a player of their own.
   */
  | {
      readonly kind: 'move-card';
      readonly target?: CardRef;
      /**
       * The "each …" form: "they discard their hands", "put the top card of your deck into
       * your inkwell". Evaluated as the effect resolves, in the selector's pool order.
       */
      readonly scope?: Selector;
      readonly to: Zone;
      /** Decks are ordered: `bottom` is "on the bottom of your deck". Default `top`. */
      readonly placement?: ZonePlacement;
      /** "…and exerted". Only meaningful in play or the inkwell. */
      readonly exerted?: boolean;
      /** "…facedown": knowledge is **not** granted. */
      readonly facedown?: boolean;
      /** Shuffle the destination deck afterwards ("shuffle it into its player's deck"). */
      readonly shuffle?: boolean;
      /**
       * "…in a random order" (ticket 103, ruby's Come Out and Fight!) — randomizes only the
       * relative order **among the cards this effect moves**, leaving the rest of the
       * destination zone untouched. Distinct from `shuffle`, which reshuffles the whole
       * destination deck: "put all cards from under chosen character on the bottom of their
       * deck in a random order" must not disturb the cards already sitting there. Meaningful
       * only with `scope` (a `target`'s single card has no order to randomize) and only visibly
       * different in an ordered zone (`deck`); harmless elsewhere.
       */
      readonly randomizeOrder?: boolean;
    }
  /**
   * "Reveal a card" / "look at the top N cards of your deck". Grants knowledge and nothing
   * else — the cards do not move. `controller` is "look at", `all` is "reveal" (CR §7.9).
   *
   * The difference from `look-at-deck` is what the rest of the sentence then names them with:
   * this one leaves the pool to a positional `Selector` (`topOfDeck`), while `look-at-deck`
   * opens a *looking* window the interpreter closes again (`Selector.looking`).
   */
  | {
      readonly kind: 'reveal';
      /** One named card — "reveal that card", where the card came from a choice. */
      readonly target?: CardRef;
      /** A pool — "reveal the top 4 cards of your deck", "reveals their hand". */
      readonly scope?: Selector;
      readonly to: 'controller' | 'all';
    }
  /** "Shuffle your deck" (CR §7.8) — order only; nothing moves between zones. */
  | { readonly kind: 'shuffle'; readonly player: PlayerRef }
  /**
   * "You may pay 1 {I}" — a payment made **inside** an effect rather than as the cost of
   * playing a card (CR §6.1.7). Pair it with `can-pay-ink` to keep the offer legal.
   */
  | { readonly kind: 'pay-ink'; readonly amount: Amount; readonly player?: PlayerRef }
  /**
   * "You may play that character for free" (CR §6.1.7) — from wherever it is now, without
   * paying anything. Unlike `grant-free-play`, which is a payment *permission* for a future
   * play action, this plays the named card straight away.
   *
   * `destination` is "if you do, put that card on the bottom of your deck instead of into your
   * discard" (Ursula - Deceiver of All, ticket 127) — where an **Action** goes once its own
   * effect finishes, read by `engine.ts`'s finishing step in place of the default discard.
   * Absent is that ordinary discard.
   */
  | {
      readonly kind: 'play-free';
      readonly target: CardRef;
      readonly exerted?: boolean;
      readonly destination?: 'bottom-of-deck';
    }
  /**
   * Shift the named character for free as a resolving effect. The card is played at cost 0,
   * but lands on the named legal base through the ordinary Shift stack machinery instead of
   * entering as a new base character.
   */
  | { readonly kind: 'shift-free'; readonly target: CardRef; readonly onto: CardRef }
  /**
   * "You may play her **and she enters play exerted**" — the same play from wherever the card
   * is now, but for its **real printed cost** rather than for free (CR §4.3.3, §6.1.7). The
   * ink is taken the moment the effect resolves, so an offer the controller cannot afford
   * does nothing at all (CR §1.2.3) — the same answer `can-pay-ink` gives one step earlier,
   * which is what keeps the offer off the option list to begin with.
   */
  | { readonly kind: 'play-paying'; readonly target: CardRef; readonly exerted?: boolean }
  /**
   * "Whenever one of your characters quests **this turn**, …" — a delayed/floating triggered
   * ability created by a resolving effect (CR §6.2.7). It is registered against the card whose
   * effect created it (an Action, usually already in the discard), so its condition is scoped
   * `anywhere`, and it is dropped at its duration's boundary.
   *
   * `closeOver` names bindings from the **registering** frame ("chosen character", "the card
   * just played for free") that the delayed effect's own `bound(id)` reads need to survive
   * into — the trigger fires as a fresh `AbilityInstance` with its own empty binding list, so
   * without this a `bound(id)` inside `effect` finds nothing once the trigger fires (ticket
   * 106). Named explicitly, not "every current binding": a delayed effect that runs its own
   * `chosen…()` picks a fresh id, and an implicitly-forwarded stale one of the same name would
   * shadow it (the `.find()` in `resolveCardRefAll` returns the first match).
   */
  | {
      readonly kind: 'delayed-trigger';
      readonly when: TriggerCondition;
      readonly effect: readonly Effect[];
      readonly duration: Duration;
      readonly label?: string;
      readonly closeOver?: readonly string[];
    }
  // -- characteristics: an *applied* static (CR §6.4.2.2) --------------------
  /**
   * "Chosen character gets +2 {S} this turn."
   *
   * Applied statics **snapshot**: the cards this affects are fixed when the effect resolves,
   * so a character that arrives afterwards is not affected. `target` names them through the
   * ordinary reference machinery; `scope` is the "all/each …" form, whose pool is evaluated
   * once, here, and frozen into the modifier record (`modifiers.ts`).
   */
  | {
      readonly kind: 'modify';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly stat: Stat;
      readonly amount: Amount;
      readonly duration: Duration;
      /** "while …" — re-evaluated on every read, so the bonus flips on and off (CR §6.1.13). */
      readonly guard?: Condition;
      /** Keep the `Amount` live instead of counting it once at resolution (CR §6.1.8). */
      readonly dynamic?: boolean;
    }
  | {
      readonly kind: 'grant-keyword';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly keyword: KeywordName;
      readonly value?: number;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  /**
   * "…and loses Reckless this turn" (CR §6.6.5) — the mirror of `grant-keyword`. It suppresses
   * the keyword whatever its source, printed included, for as long as the record lives.
   */
  | {
      readonly kind: 'lose-keyword';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly keyword: KeywordName;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  /**
   * "…gains the Detective classification until the start of your next turn" (CR §5.3.3.2) —
   * a *classification* granted rather than a stat or a keyword. Same record shape as the rest;
   * `Selector.subtype`/`anySubtype` read printed and granted classifications together.
   */
  | {
      readonly kind: 'grant-subtype';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly subtype: string;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  /**
   * "They can't quest for the rest of this turn", "that character can't ready at the start of
   * their next turn" — a continuous effect that removes a *permission* rather than changing a
   * characteristic (CR §6.6.5). Same record shape as a stat modifier; read by the legality
   * predicates instead of the characteristic accessors.
   */
  | {
      readonly kind: 'restrict';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly restriction: Restriction;
      /**
       * For `be-challenged`: which challengers are barred ("Damaged characters can't …"). For
       * `sing`: which songs are exempt ("…can't sing songs without Sing Together", ticket 139).
       */
      readonly by?: Selector;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  /** A temporary permission that lifts one ordinary legality limiter (CR §6.6.5). */
  | {
      readonly kind: 'allow';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly permission: Permission;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  /**
   * "This turn, you may ink an additional card" — a permission given to a **player** for a
   * duration (CR §6.6.5), rather than to any card.
   *
   * The record it leaves behind is scoped to the player, not to a snapshot of cards, so it has
   * nothing to lose when its source leaves play: the Action that granted it is discarded the
   * moment it finishes resolving, and the permission still stands until its duration runs out.
   * Everything else is an ordinary modifier record — same provenance, same durations, same
   * `guard` re-read on every read (`modifiers.ts`).
   */
  | {
      readonly kind: 'allow-player';
      readonly permission: PlayerPermission;
      /** Whose permission it is. Default the ability's controller. */
      readonly player?: PlayerRef;
      /** How many times over — a second "ink an additional card" adds another. Default 1. */
      readonly amount?: number;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  /**
   * "Each opponent can't ready more than N of their characters at the start of their next
   * turn" (Marshmallow - Cranky Climber) — a numeric cap on the Ready step given to a
   * **player** rather than to any card (CR §6.6.5), the restriction-side mirror of
   * `allow-player`. `readyStep` reads it through `readyCapOf` and, when more characters are
   * eligible than the cap allows, asks the capped player which of them ready.
   */
  | {
      readonly kind: 'cap-ready';
      readonly amount: number;
      /** Who is capped. Default the ability's opponent — every printed instance says "each opponent". */
      readonly player?: PlayerRef;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  /**
   * "Chosen character gains '[ability]' this turn" (CR §6.4.3) — a whole ability granted to
   * another card. The granted ability is ordinary IR, so it behaves exactly as it would if it
   * were printed; a *triggered* one is registered as the grant lands and dropped as it ends.
   */
  | {
      readonly kind: 'grant-ability';
      readonly target?: CardRef;
      readonly scope?: Selector;
      readonly ability: Ability;
      readonly duration: Duration;
      readonly guard?: Condition;
    }
  // -- locations (CR §4.3.6, §5.6) ------------------------------------------
  /**
   * "Move [character] to [location]" — the move a card's effect performs, which pays no move
   * cost (the turn action in `rules.ts` is where the cost is paid). A character already at
   * another location simply moves; a target that is not a character, or a location that has
   * left play, does nothing (CR §1.2.3).
   */
  | {
      readonly kind: 'move-to-location';
      readonly target: CardRef;
      readonly location: CardRef;
    }
  // -- payments (ticket 09 — the cost system; `costs.ts` holds the records) --
  /**
   * A floating payment modifier (CR §4.3.2.2): "you pay 1 {I} less for the next character you
   * play this turn", "your opponents pay 1 {I} more to play actions". `amount` is negative to
   * reduce; `oneShot` is the "the next …" shape, spent by the payment it applies to.
   *
   * `filter` and `target` are the payment side of `modify`'s `scope`/`target` split: a `filter`
   * re-matches whatever has those printed characteristics whenever a payment is priced, while a
   * `target` **binds the record to the cards it names as the effect resolves** — "chosen
   * character counts as having +3 cost to sing songs this turn" follows that one instance and
   * no other, not even a second copy of the same card in play.
   */
  | {
      readonly kind: 'modify-cost';
      readonly filter?: Selector;
      readonly target?: CardRef;
      readonly amount: Amount;
      readonly applies?: CostApplies;
      readonly duration: Duration;
      readonly player?: PlayerRef;
      readonly oneShot?: boolean;
    }
  /** "You may play [X] for free" (CR §6.1.7) — a permission, not a discount. */
  | {
      readonly kind: 'grant-free-play';
      readonly filter: Selector;
      readonly duration: Duration;
      readonly player?: PlayerRef;
      readonly oneShot?: boolean;
    }
  /**
   * "Opponents can't play actions until the start of your next turn" — a **play** permission
   * removed, so it belongs with the payment records in `costs.ts` rather than with the
   * modifiers on cards: it is scoped to a player and a printed-card filter, not to anything in
   * play, and `legalPlayOptions` is the one reader.
   */
  | {
      readonly kind: 'forbid-play';
      readonly filter: Selector;
      readonly duration: Duration;
      readonly player?: PlayerRef;
    }
  // -- escape hatch (ticket 13) --------------------------------------------
  /** Names a generator in the `custom()` registry. Unresolvable until ticket 13. */
  | { readonly kind: 'custom'; readonly id: string };

/**
 * `Ability` is declared below `Effect` because an ability's effect is an `Effect` tree; the
 * `grant-ability` node above closes the loop, which TypeScript is fine with and which is what
 * "a card may grant another card an ability" means.
 */

/**
 * Which payment a cost modifier applies to (CR §4.3.2.2). `play` — the cost of playing a
 * card, whichever method pays it; `sing` — the value a singer must reach (CR §8.11.3's "+N
 * cost to sing"); `ability` — the `{I}` part of an activated ability's cost; `shift` — the
 * Shift alternate cost **only** ("you pay 1 {I} less to play this character using her Shift
 * ability"), which a plain `play` reduction would also have reached but which this one does
 * not touch when the card is played the ordinary way; `move` — a location's move cost
 * (CR §4.3.6), whose payment subject is the **destination** location and which may
 * additionally key on the mover through `cost-modifier`'s `mover` filter; `quest` and
 * `challenge` — an additional ink cost attached to that attempt, with the acting character
 * as the payment subject ("unless you pay N {I}. You pay this cost each time.").
 */
export type CostApplies = 'play' | 'sing' | 'ability' | 'shift' | 'move' | 'quest' | 'challenge';

/** Ticket 07's name for one node of a trigger's effect list. */
export type EffectStep = Effect;

// ---------------------------------------------------------------------------
// Triggers
// ---------------------------------------------------------------------------

/**
 * When a triggered ability's condition is met (CR §6.2).
 *
 * Four orthogonal slots, as the ability-representation analysis (§1.2) requires — a flat
 * event enum cannot express `During each opponent's turn, whenever they draw a card while
 * this character is exerted, …`:
 *
 * - **`event`** (+ `subject`/`filter`) — which emitted `GameEvent` meets it;
 * - **`window`** — whose turn it must be;
 * - **`guard`** — a state predicate checked at trigger time;
 * - **`limit`** — "once per turn", tracked per **ability instance** (CR §6.1.13.2).
 */
export interface TriggerCondition {
  /** The emitted event that meets the condition — `character-quested`, `card-banished`, … */
  readonly event: GameEventType;
  /**
   * Which of those events count:
   * - `self` — about the ability's own card ("Whenever **this character** quests")
   * - `controller` — about a card or player under the ability's controller ("whenever you …")
   * - `opponent` — about an opposing card or player
   * - `any` — every event of that type ("whenever a character is banished")
   */
  readonly subject: 'self' | 'controller' | 'opponent' | 'any';
  /**
   * Where the ability's source must be for the trigger to fire. `in-play` is what an
   * ordinary printed ability wants; `anywhere` is what a "when this is banished" ability
   * needs, since by collection time the card is already in the discard.
   *
   * `discard` is the third reading, and the only one that also changes *registration*: an
   * ability that functions from the discard (CR §6.1.12) — "At the start of your turn, if
   * this card is in your discard, …" — is registered when the card **enters** a discard
   * rather than when it enters play, and fires only while it is still there.
   *
   * `own-shift` is the fourth and narrowest reading (ticket 80): "when you play a [X] character
   * on this card" — a Shift base's own printed ability reacting to the very Shift that buries
   * it. A card under another via Shift has none of its abilities function once it's in the
   * `under` zone (CR §8.10.4), but this condition is met at the step of the Shift that puts it
   * there (CR §4.3.4.8), not after. It is a one-shot exception, not a loosening of the general
   * rule: `bag.ts`'s `triggerMatches` only lets it through for the exact `card-put-under` event
   * that moved *this* card into `under`, and every other event this card might try to react to
   * once buried still hits the ordinary disablement.
   *
   * **Defaults to `anywhere`** — ticket 07's registrations were unscoped, and the DSL sets
   * this explicitly on every ability it builds.
   */
  readonly where?: 'in-play' | 'anywhere' | 'discard' | 'own-shift';
  /** A further predicate on the card the event was about ("whenever a *Broom* character …"). */
  readonly filter?: Selector;
  /**
   * On a `damage-dealt` event, which verbs count (CR §1.9). Absent means the umbrella
   * reading — "whenever this character **takes** damage", by any means. `['deal']` is the
   * narrower "whenever this character is **dealt** damage", which put-damage does not meet.
   */
  readonly damageVerb?: readonly DamageVerb[];
  /** Whose turn it must be. Default `any-turn`. */
  readonly window?: 'your-turn' | 'opponent-turn' | 'any-turn';
  /**
   * On a `phase-began` event, which phase counts. **"At the start of your turn" is one phase,
   * not three**: without this slot the event fires again at the main phase and again at the
   * end-of-turn phase, and the ability would resolve three times a turn.
   */
  readonly phase?: TurnPhase;
  /** A state predicate checked when the condition is met. */
  readonly guard?: Condition;
  /**
   * "Once per turn" / "twice during your turn" (CR §6.1.13.2, Tod - Knows All the Tricks'
   * IMPRESSIVE LEAPS), counted per registered ability instance. Both readings share one
   * counter in `state.abilityUses` (`bag.ts`'s `TRIGGER_USE_LIMITS`) — the string form is
   * only sugar over a max-uses count, so a third "per turn" reading needs no new machinery,
   * just another entry in that map.
   */
  readonly limit?: 'once-per-turn' | 'twice-per-turn';
  /**
   * "Whenever your opponent discards **1 or more** cards, …" (ticket 124, Prince John -
   * Greediest of All) — fold every match of this trigger that arrives while an earlier one is
   * still sitting unresolved in the bag into that same item, instead of adding a second one.
   *
   * The event this engine emits per discarded card (`card-discarded`) is per-**card**, but the
   * printed ability is one all-or-nothing decision over the whole discard **action** — which
   * can itself span several `apply`s (`discardChosen`'s one-card-at-a-time choice). The bag
   * stays closed for the entire span (`gsc.ts`'s `bagWindowOpen` is false mid-effect), so
   * "still unresolved" is exactly "still part of the same action": `bag.ts`'s `addToBag` merges
   * into the waiting item and bumps its `eventAmount`, which is what `triggeringAmount()` reads
   * once the ability resolves ("draw a card for **each** card discarded").
   */
  readonly batch?: boolean;
}

// ---------------------------------------------------------------------------
// Activation costs
// ---------------------------------------------------------------------------

/**
 * One component of an activated ability's cost (CR §4.4).
 *
 * The general cost system (ticket 09) lives in `costs.ts`/`payments.ts`: **alternate costs
 * attach to playing a card**, which is where the CR puts them (§1.5.5), and payment modifiers
 * reach an ability's `{I}` through `abilityInkCostOf`.
 *
 * A component that needs a **choice** to pay — "Banish 2 of your items —" — follows the
 * recipe `payments.ts` set for playing a card: every legal way to pay it is enumerated as its
 * own keyed option (`activate:i7:a0:banish:i3+i4`), never as a follow-up question.
 */
export type ActivationCost =
  /** `{E}` — exert the source. Characters must be dry (CR §4.4.2); items need not be. */
  | { readonly kind: 'exert' }
  /** `N {I}` — exert that many ready ink cards. */
  | { readonly kind: 'ink'; readonly amount: number }
  /** "Banish this item" and friends. */
  | { readonly kind: 'banish-self' }
  /**
   * "Banish 2 of your items —" (CR §4.4): banish this many cards you control matching the
   * filter. The **cards are chosen**, so the activation option names them: one option per
   * legal set, which is what keeps the decision a single keyed action.
   */
  | {
      readonly kind: 'banish-cards';
      readonly filter: Selector;
      /** How many to banish. Default 1. */
      readonly count?: number;
    }
  /**
   * "⟳ one of your characters —", "⟳ one of your items —" (CR §4.4): exert this many cards
   * you control matching the filter, chosen the same way `banish-cards` is — one option per
   * legal set. Mirrors `banish-cards` exactly except for the verb it pays with. A character
   * pays only when dry (CR §4.4.2); an item or location need only be ready (CR §5.5).
   */
  | {
      readonly kind: 'exert-cards';
      readonly filter: Selector;
      /** How many to exert. Default 1. */
      readonly count?: number;
    }
  /**
   * "Discard a card —" (CR §4.4, §7.3): discard this many cards from your hand, chosen at
   * payment time the way `banish-cards` chooses in play — one option per legal set. The hand
   * being hidden, a set is named by **printed ids** (SPEC.md §4.4), so two copies of one card
   * are the same way to pay. With too few cards in hand the activation is simply not offered:
   * a cost that cannot be paid makes the whole activation illegal, never a no-op.
   */
  | {
      readonly kind: 'discard-cards';
      /** How many to discard. Default 1. */
      readonly count?: number;
      /**
       * "Discard **an item card** —" (The Wardrobe - Perceptive Friend): restricts which of the
       * controller's hand cards are legal to discard as the cost, mirroring `banish-cards`'
       * `filter`. Because the pool is always the hand, not a selector-scoped zone, the selector
       * must say so itself — write `zone: 'hand'` the same way an "in your hand" filter does
       * anywhere else (SPEC.md §4.4). Undefined means any card, the prior unfiltered behavior.
       */
      readonly filter?: Selector;
    }
  /**
   * "Deal 1 damage to this character —" (CR §4.4): the source deals this much damage to
   * itself as part of paying the cost. Goes through the same `dealDamage` pipeline as any
   * other damage — Resist reduces it, replacements see it — and is unconditionally payable
   * exactly like `banish-self`: the CR has no "can't pay a cost that would banish the source"
   * carve-out (banish-self costs are the existing proof), so a would-be-lethal amount is paid
   * in full and the normal game state check sweeps the source afterward if it turns out lethal.
   */
  | { readonly kind: 'self-damage'; readonly amount: number }
  /**
   * "…may pay 3 lore —" (Hercules - Manipulated Hero's EVERYTHING HAS A PRICE): the activator
   * loses this much lore, through the same `lose-lore` chokepoint any other lore loss goes
   * through (`loseLore`, `damage.ts` — ticket 57's `gainLoreEvent` sibling), so a replacement
   * watching for a lore loss sees this too. **Paid by whoever activates the ability**, not
   * necessarily the card's controller — for an ordinary `your-turn`/`any-turn` ability that is
   * the same player either way, but an `opponent-turn` ability (see `ActivatedAbility.window`)
   * is activated, and so paid, by the non-controller. Payable only down to what the payer
   * actually has (CR §6.6.3, lore never goes negative): unlike `self-damage`/`banish-self`,
   * there is no "pay it anyway and let the GSC clean up" reading — an unaffordable amount just
   * makes the activation illegal, like `banish-cards`/`exert-cards`/`discard-cards`.
   */
  | { readonly kind: 'pay-lore'; readonly amount: number };

// ---------------------------------------------------------------------------
// Static abilities
// ---------------------------------------------------------------------------

/**
 * A continuous static ability from a card in play (CR §6.4.2.1): it applies dynamically, so
 * a card that arrives later is affected too. Computed on read, never stored (SPEC.md §4.5).
 *
 * `filter` is a live selector — it reads *modified* characteristics, so "your characters with
 * 2 {S} or less get +1 {W}" sees another static's work (CR §6.4.4's dependent effects).
 * `amount` is likewise live: a static's "for each" is counted continuously, not once
 * (CR §6.1.8), and it may name a characteristic of the card being read (`subject`) — the
 * "+{S} equal to their {W}" family.
 *
 * *Applied* statics — the snapshot kind produced by a resolved effect — are the `modify` /
 * `grant-keyword` effects plus a modifier record; see `modifiers.ts`.
 */
export type StaticEffect =
  | {
      readonly kind: 'buff';
      readonly filter: Selector;
      readonly stat: Stat;
      readonly amount: Amount;
    }
  | {
      readonly kind: 'grant-keyword';
      readonly filter: Selector;
      readonly keyword: KeywordName;
      /**
       * The keyword's "+N", counted continuously like `buff`'s `amount` (CR §6.1.8) — "gains
       * Resist +1 for each location you have in play" is `count(…)` here, not a snapshot.
       */
      readonly value?: Amount;
    }
  /**
   * "Characters named Peter Pan lose Evasive and can't gain Evasive" (CR §6.6.5's "loses"
   * pattern, applied to a keyword instead of a whole named ability) — the continuous mirror of
   * `grant-keyword`, but subtractive, and the continuous-static half of `lose-keyword`'s applied
   * record. While this static is live, every matching card loses the named keyword however it
   * has it — printed, authored or granted — and the denial ("can't gain") falls out for free:
   * `keywordsOf` (`keywords.ts`) filters *lost* keywords out of `base.concat(granted)` on every
   * read, after every grant has already been folded in, so a later `grant-keyword` — applied or
   * continuous — never survives the filter while this static's `filter` still matches. No
   * separate "denial" record is needed, the same way `suppress-ability` needs no separate record
   * to stop a suppressed ability from being re-granted.
   */
  | {
      readonly kind: 'lose-keyword';
      readonly filter: Selector;
      readonly keyword: KeywordName;
    }
  /**
   * "Your other characters gain the Detective classification" (CR §5.3.3.2, §6.4.2.1) — the
   * continuous mirror of the applied `grant-subtype` effect. Unlike that effect's fixed-duration
   * modifier record, this is re-derived on every read like `grant-keyword`: a character that
   * arrives later has the classification too, and the grant disappears the instant this card's
   * source leaves play (there is nothing left in `GameState` to clean up).
   */
  | {
      readonly kind: 'grant-subtype';
      readonly filter: Selector;
      readonly subtype: string;
    }
  /**
   * "Your Gargoyle characters lose the Stone by Day ability" (CR §6.6.5's "loses" pattern,
   * applied to a whole **named** ability instead of a keyword) — the continuous mirror of
   * `grant-keyword`, but subtractive: while this static is live, every matching card loses the
   * named ability entirely, whatever gave it to them (printed or granted), leaving every other
   * ability and every characteristic untouched.
   *
   * `name` is matched by normalized printed name (`normaliseAbilityName` in `modifiers.ts`),
   * not display text — the referencing card's body text is title-cased ("Stone by Day") while
   * the printed ability header is shouted ("STONE BY DAY"), and a stable identity match keeps
   * both readings the same ability. Read at every ability-lookup path a card has:
   * `abilitiesOf`/`registerAbilitiesFor` (`interpret.ts`), `replacementsOf` (`replacements.ts`)
   * and `activeStaticAbilitiesOf` (`modifiers.ts`) — so a suppressed ability is gone from
   * triggers, replacements and continuous statics alike, not just one of them. Two suppressors
   * naming the same ability, or a suppressed name matching both a printed and a granted copy,
   * simply collapse: `suppressedAbilityNames` is a deduplicated set, and any hit removes every
   * instance of that name.
   */
  | {
      readonly kind: 'suppress-ability';
      readonly filter: Selector;
      readonly name: string;
    }
  /**
   * A continuous **payment** modifier: "your Princess characters cost 1 {I} less to play",
   * or "this character can't quest unless you pay 1 {I}".
   *
   * `from` is where this card must be for the ability to function (CR §6.1.12): `play` for an
   * ordinary static, `hand` for an Underdog-shaped one, `discard` for "while this is in your
   * discard, …". Read by `costs.ts`, never by the characteristic accessors — a cost modifier
   * changes what you *pay*, not the card's cost characteristic.
   */
  | {
      readonly kind: 'cost-modifier';
      readonly filter: Selector;
      /**
       * Negative reduces; positive adds a cost. An `Amount` node is counted **continuously**,
       * like every other static's amount (CR §6.1.8) — "for each exerted character opponents
       * have in play, you pay 1 {I} less to play this character" is `minus(count(…))`.
       */
      readonly amount: Amount;
      readonly applies?: CostApplies;
      readonly from?: Zone;
      /**
       * For `applies: 'move'` only: a second filter matched against the **moving character**
       * rather than the payment's subject (the destination location) — "your characters named
       * Robin Hood may move here for free" keys on who is moving, not where. Absent means any
       * mover, which is what the destination-keyed wave-5 cards print.
       */
      readonly mover?: Selector;
      /**
       * For `applies: 'shift'` only: a second filter matched against the **chosen Shift base**
       * rather than the payment's subject (the card being played) — `mover`'s onto-keyed twin.
       * "You pay 1 {I} less to shift a character on top of this character" (Belle - Always
       * Reading's DREAMING OF MORE) keys on which base the Shift lands on, not on the card being
       * shifted. Checked against every base in a Duo/Combo Shift's pair — either one landing on
       * the named base is enough. Absent means any base, which is what a plain `applies: 'shift'`
       * reduction already covers.
       */
      readonly onto?: Selector;
      /**
       * "Once during your turn, you pay 1 {I} less to move this character" (CR §6.1.13.2) —
       * tracked the same way an activated ability's own `limit: 'once-per-turn'` is: a
       * this-turn usage flag on the source, spent the moment a payment this modifier actually
       * applied to goes through (never merely when a cost is priced) and cleared in the Ready
       * step. Absent means unconditional/continuous, which is every other cost-modifier static.
       */
      readonly limit?: 'once-per-turn';
    }
  /**
   * "You may have up to 99 copies of [this card] in your deck" (CR §3.1.2's deck-construction
   * exception). A **deck-building** rule, not a game rule: nothing in a running game reads it,
   * which is why it has no interpreter case — the deck validator does (SPEC.md §8).
   */
  | { readonly kind: 'deck-limit'; readonly max: number }
  /**
   * "You can have [classification] characters in your deck regardless of ink type" (Christopher
   * Robin - Hunny Sage's GATHER THE PARTY) — the same kind of **deck-building** rule as
   * `deck-limit`, and for the same reason has no interpreter case: nothing in a running game
   * reads it, only the deck validator (SPEC.md §8).
   */
  | { readonly kind: 'deck-ink-rule'; readonly subtype: string }
  /**
   * "Your characters with Reckless gain '⟳ — Gain 1 lore.'" — a whole ability granted
   * continuously (CR §6.4.3), so a character that arrives later has it too.
   */
  | { readonly kind: 'grant-ability'; readonly filter: Selector; readonly ability: Ability }
  /**
   * "This character can't quest unless …", "opponents can't play actions" — a continuous
   * restriction (CR §6.6.5). Dynamic like every other static: the cards it forbids are
   * whatever matches `filter` at the moment the legality question is asked, and wrapping it in
   * `whileActive` is what makes an "unless" clause flip it off again.
   */
  | {
      readonly kind: 'restrict';
      readonly filter: Selector;
      readonly restriction: Restriction;
      /** For `be-challenged`: which challengers are barred ("Damaged characters can't …"). */
      readonly by?: Selector;
    }
  /**
   * "Opponents must choose this character for actions and abilities if able" (ticket 119,
   * John Smith - Undaunted Protector's DO YOUR WORST) — a mandatory-target priority, the
   * general-targeting sibling of Bodyguard's must-choose challenge limiter (CR §8.2.2).
   *
   * Read by `targetPool` (`abilities/selectors.ts`), which narrows an already-legal pool down
   * to the matching cards whenever any are present, and falls back to the full pool otherwise
   * — "if able" is exactly that fallback. Composes for free with everything `targetPool`
   * already applies in order: Ward and other legality limiters run first (through
   * `canBeChosenBy`/`selectorPool`), so a warded or selector-excluded priority card is simply
   * absent from what this filter sees, and the ordinary fallback takes over. Two live
   * priority effects both stay in the narrowed pool — like two Bodyguards, this does not
   * pick a winner between them.
   *
   * Deliberately **not** consulted by `legalChallengeTargets`: a challenge is not "choosing"
   * for this purpose, mirroring Ward (CR §8.15), and Bodyguard already owns the
   * challenge-target-priority mechanism this static does not duplicate.
   */
  | {
      readonly kind: 'must-be-chosen';
      readonly filter: Selector;
    }
  /**
   * A continuous permission — the live mirror of the `allow` effect, matched like every
   * other static so a card that arrives later has it too.
   *
   * `condition` narrows *which* targets the permission covers, matched against the potential
   * challenge target rather than against `filter`'s source cards — "…can challenge ready
   * **damaged** characters" (Gizmoduck - Suited Up) needs "ready" alone to keep meaning "ready"
   * while a second, independent predicate about the defender is layered on top. Absent, the
   * permission is unconditional, which is every other card that prints this sentence.
   */
  | {
      readonly kind: 'allow';
      readonly filter: Selector;
      readonly permission: Permission;
      readonly condition?: Selector;
    }
  /**
   * "During your turn, you may ink an additional card" — raises its controller's CR §4.2
   * per-turn ink limit by `amount` while this card is in play. **Player-scoped**, so it has
   * no card `filter`: the limit belongs to the turn, not to a card, and `inkLimitOf`
   * (`modifiers.ts`) counts the live copies on every read — each copy grants its own
   * addition, so two raise the limit to 3. "During your turn" is the ability's ordinary
   * `while` guard, not part of this node.
   */
  | { readonly kind: 'extra-ink'; readonly amount: number }
  /**
   * "All cards in your hand count as having ◉" — every card in its controller's hand passes
   * the CR §4.2 inkwell-symbol check while this card is in play. **Player-scoped** like
   * `extra-ink`, so it has no card `filter`: the override covers the whole hand, cards that
   * arrive later included, and `handCountsAsInkable` (`modifiers.ts`) reads the live copies
   * on every legality check — nothing else about inking (the per-turn limit, the facedown
   * placement) is touched.
   */
  | { readonly kind: 'hand-inkable' }
  /**
   * "You can ink cards from your discard" (Moana - Curious Explorer's ANCESTRAL LEGACY) —
   * adds its controller's discard pile as a legal origin for the ordinary CR §4.2 ink turn
   * action while this card is in play. **Player-scoped** like `hand-inkable`, so it has no
   * card `filter`: the override covers the whole discard, cards that land there later
   * included, and `discardCountsAsInkable` (`modifiers.ts`) reads the live copies on every
   * legality check. Everything else about inking is untouched — the printed inkwell symbol
   * is still required (this only adds a zone, not a symbol override), the once-per-turn
   * limit is still `inkLimitOf`'s question, and the card still lands through the ordinary
   * `inkEvent` chokepoint (`rules.ts`) facedown like any other ink.
   */
  | { readonly kind: 'discard-inkable' }
  /**
   * "Each player plays with the top card of their deck face up" — the covered decks' top
   * card is continuously **revealed to everyone** while this card is in play. **Player-scoped**
   * like `extra-ink`, so it has no card `filter`: which card is face up follows the deck as it
   * changes (draws, shuffles, tucks), which is why nothing is snapshotted. `deckTopIsRevealed`
   * (`modifiers.ts`) reads the live copies; the engine keeps `knownTo` caught up between steps
   * and `playerView` names the top card positionally for as long as a copy is live (`view.ts`).
   * `decks: 'opponent'` is the third reading — "each opponent plays with the top card of their
   * deck face up" (Roz - Always Watching) — the mirror of `'your'`, naming decks belonging to
   * anyone *but* the source's controller.
   */
  | { readonly kind: 'reveal-deck-top'; readonly decks: 'your' | 'each' | 'opponent' }
  /**
   * "Each opposing player can't gain lore unless one of their characters has challenged this
   * turn" — a **player-scoped** restriction on *gaining* lore (CR §6.6.5's "can't"), so it has
   * no card `filter`: the permission belongs to a player, not to a card. `canGainLore`
   * (`modifiers.ts`) reads the live copies at the one chokepoint every gain passes through
   * (`gainLoreEvent`, `damage.ts`), so questing, a location's ◊ and an effect's "gain N lore"
   * are all stopped alike — and a stopped gain simply does not happen: no lore moves and no
   * `lore-changed` is emitted.
   *
   * `players` is read relative to this card's controller. `unless` is the printed exception,
   * evaluated **from the restricted player's seat** — "you" is them, because the text always
   * speaks about them ("unless one of *their* characters has challenged this turn") — while
   * `self` still names the card the ability is printed on. The ability's own "While this
   * character is exerted" stays an ordinary `while` guard, read against its controller as
   * every other static's is.
   */
  | {
      readonly kind: 'restrict-lore-gain';
      /** Whose lore gain is stopped, relative to this card's controller. */
      readonly players: 'you' | 'opponents' | 'each';
      /** "unless …" — while this holds for the restricted player, the gain goes through. */
      readonly unless?: Condition;
    }
  /**
   * "Opponents need 25 lore to win the game" (Donald Duck - Flustered Sorcerer, CR §2.5) — a
   * **player-scoped** raise of the CR §1.8.1.1 lore-to-win threshold, so it has no card
   * `filter`: the threshold belongs to a player, not to a card. `loreToWinOf` (`modifiers.ts`)
   * adds up the live copies on every read, the same discipline as `extra-ink` raising the ink
   * limit — each copy adds its own `amount` on top of `state.rules.loreToWin`, so two raise a
   * covered player's threshold by twice as much.
   *
   * `players` is read relative to this card's controller, exactly as `restrict-lore-gain`'s is.
   */
  | {
      readonly kind: 'raise-lore-to-win';
      /** Whose threshold is raised, relative to this card's controller. */
      readonly players: 'you' | 'opponents' | 'each';
      readonly amount: number;
    }
  /**
   * "Skip your turn's Draw step" (Arthur - Determined Squire) — a **player-scoped** suppression
   * of the CR §3.2.3 Draw step, so it has no card `filter`: the step belongs to a player, not
   * to a card. `drawStepSkipped` (`modifiers.ts`) reads the live copies at the one chokepoint
   * the Draw step passes through (`rules.ts`'s `drawStep`), the same discipline as `inkLimitOf`
   * and `handCountsAsInkable` — computed on read, nothing cached, so the suppression lifts the
   * instant the source leaves play.
   *
   * Always the controller's own Draw step ("your turn's") — unlike `restrict-lore-gain`'s
   * `players`, no printed card spells this out for opponents, so there is nothing to
   * parametrize.
   */
  | { readonly kind: 'skip-draw' }
  /**
   * "During challenges, your characters deal damage with their {W} instead of their {S}" —
   * a **player-scoped** replacement for the characteristic used by the challenge Damage step.
   * It has no card `filter`: every character controlled by this static's controller uses the
   * named stat, whether attacking or defending. `challengeDamageStatOf` (`modifiers.ts`) reads
   * the live copies at damage computation time, so the override disappears immediately when
   * the source leaves play.
   *
   * Only willpower is currently expressible because it is the only printed replacement stat.
   * Keeping the stat in the IR makes the rule explicit and leaves the node extensible if a
   * future card names another characteristic.
   */
  | { readonly kind: 'challenge-damage-stat'; readonly stat: 'willpower' }
  /**
   * "This character also counts as being named King Candy **for Shift**" (CR §8.10.3) — a name
   * added to a card, and only for the one question that reads it: whether this card may be a
   * Shift base. It is deliberately not a general name grant, because the card is deliberately
   * not generally named that — "chosen character named King Candy" does not find it.
   */
  | { readonly kind: 'shift-name'; readonly filter: Selector; readonly name: string }
  /**
   * "You may play any character with Shift on this character as if this character had any
   * name" (Morph - Space Goo's MIMICRY, CR §8.10.3) — the wildcard sibling of `shift-name`:
   * rather than adding one specific name, it satisfies *every* name a Shift card's base could
   * ask for. Only the name-matching Shift kinds (plain, `Duo`, `Combo`) ever read it —
   * `Universal Shift` and `[Classification] Shift` already ignore names, and `Potato Shift`
   * only ever targets items, so a character carrying this static is still never a legal
   * `Potato Shift` base.
   */
  | { readonly kind: 'shift-wildcard-base'; readonly filter: Selector }
  /**
   * "You can shift any character on top of this character. (This includes all Shift variants.)"
   * (Morph - Little Imitator's ADVANCED MIMICRY, CR §8.10.3) — the broader sibling of
   * `shift-wildcard-base`. That static only ever answers the *name* question `same-name`/`Duo`/
   * `Combo` Shift ask; this waives base eligibility outright, including `[Classification] Shift`'s
   * subtype check, which no name-based static could satisfy. `Universal Shift` already accepts
   * any base, and `Potato Shift` only ever targets items, so a character carrying this static is
   * still never a legal `Potato Shift` base — the item-type check in `isShiftBase` runs first
   * and is untouched by this node.
   */
  | { readonly kind: 'shift-any-base'; readonly filter: Selector }
  /**
   * "During your turn, you may banish chosen item of yours to play this character for free" —
   * an **alternate cost** (CR §1.5.5): a complete substitute for the ink cost, paid by
   * banishing cards, exerting cards (Scrooge McDuck - Resourceful Miser's PUT IT TO GOOD USE),
   * or moving a card from discard to the bottom of its deck (Hand-in-the-Box - Sid's Toy),
   * rather than by exerting ink. The payment is a discriminated union so every new payment
   * verb brings its own selector without creating invalid combinations of optional fields.
   *
   * It lives on the card being played, so it is read out of the **hand** (CR §6.1.12) by
   * `costs.ts` rather than off a card in play, and `payments.ts` turns each legal set of
   * candidate cards into its own keyed payment option.
   *
   * `for` says which cost this substitutes: the ordinary ink cost (`'play'`, the default and
   * every card before ticket 136) or the **Shift** cost (`'shift'`) — "you may put 5 character
   * cards from your discard on the bottom of your deck in any order to shift this character
   * for free" (Maleficent & Diablo - Evil Incarnate's FOOLS!). A `'shift'` alternate cost never
   * substitutes for an ordinary play — the printed text names Shift specifically — so
   * `payments.ts` enumerates it only alongside a Shift base, never as a standalone free play.
   */
  | {
      readonly kind: 'alternate-cost';
      readonly payment:
        | { readonly kind: 'banish'; readonly filter: Selector }
        | { readonly kind: 'exert'; readonly filter: Selector }
        | { readonly kind: 'move-to-bottom-of-deck'; readonly filter: Selector };
      /** How many. Default 1. */
      readonly count?: number;
      /** "During your turn" — when the alternate cost may be taken. Default `any-turn`. */
      readonly window?: 'your-turn' | 'any-turn';
      /** Which cost this replaces. Default `'play'`. */
      readonly for?: 'play' | 'shift';
    }
  /**
   * "Your characters' {S} can't be reduced below their printed value" (Elisa Maza - Transformed
   * Gargoyle's FOREVER STRONG) — a continuous static naming a stat that may never total less
   * than the affected card's own printed value, no matter how many reductions apply. Read live
   * by `hasPrintedFloor` (`modifiers.ts`), matched by `filter` exactly like `buff`, so a
   * character that arrives later is covered immediately and one that leaves the filter (or
   * whose source leaves play) is not — an ordinary bonus that lands afterward still raises the
   * total normally, since the floor only ever stops a *reduction*.
   *
   * Only `strength` is currently printed. Keeping the stat in the IR mirrors
   * `challenge-damage-stat`'s reasoning: the rule is generic, and only the wave of printed
   * cards is narrow.
   */
  | { readonly kind: 'stat-floor'; readonly filter: Selector; readonly stat: Stat }
  /**
   * "During your turn, you may play this character for free if …" — a continuous play-from-zone
   * **permission** (CR §6.1.7), re-evaluated on every legality check like every other static.
   * Unlike `grant-free-play`, whose resolved effect leaves a floating `costModifiers` record
   * with a duration, this permission exists exactly while the ability functions — its `while`
   * condition flips it off the moment the condition stops holding.
   *
   * `filter` is which cards its controller may play this way, read by `costs.ts`'s
   * `zonePlayPermissionFor` with the cost model's own matcher. `from` is where the printed card
   * must be for the ability to function (CR §6.1.12): `play` by default, `hand` for the shape
   * that lives on the card being played ("you may play **this character** for free …").
   *
   * `zone` is where the *target* card must be for the permission to reach it: `hand` by default
   * (every card up to ticket 140 only ever offered this from hand), `discard` for "you may play
   * characters from your discard" (The Horned King - Merciless Master's CAULDRON'S POWER).
   * `cost` is the payment policy: absent (or `'free'`) pays nothing, exactly as before; `'full'`
   * pays the card's ordinary printed cost (CR's "you pay all costs") — `costOf` already returns
   * the unmodified printed cost for a card outside its hand, so no separate pricing logic is
   * needed. `entersExerted` is "they enter play exerted" — Cauldron's Power's rider, read where
   * the play actually happens (`rules.ts`'s `playFromZonePermission`) rather than folded into a
   * generic `enter-play-ready` restriction, because that restriction can no longer tell *how* a
   * card was played once it has already moved into `play`.
   */
  | {
      readonly kind: 'free-play';
      readonly filter: Selector;
      readonly from?: Zone;
      readonly zone?: Zone;
      readonly cost?: 'full';
      readonly entersExerted?: boolean;
    }
  /**
   * "If a card left a player's discard this turn, this card gains Shift 0" (Anna - Soothing
   * Sister's UNUSUAL TRANSFORMATION, ticket 109) — a **conditional Shift grant** on the card
   * itself, re-evaluated on every legality check like every other static's `while`. It is
   * deliberately not `grant-keyword('Shift', …)`: that machinery (`modifiers.ts`'s
   * `grantedKeywords`) only ever walks cards already in play, granting only to other cards in
   * play, while this ability's whole point is a card **granting itself** Shift while sitting in
   * hand (CR §6.1.12) — the same "a static can function from hand" exception `alternate-cost`
   * and `free-play`'s `from: 'hand'` shape make, so Shift's payment surface can offer it during
   * ordinary play-option enumeration rather than only once the card is already in play.
   *
   * `cost` is the printed Shift value (`0` for every card this ticket names). `from` is where
   * the printed card must be for the grant to function — `hand` by default, since a Shift
   * payment is only ever offered while playing from hand.
   */
  | {
      readonly kind: 'grant-shift';
      readonly cost: number;
      readonly from?: Zone;
    };

// ---------------------------------------------------------------------------
// Replacement effects (CR §6.5) — the interceptor half of SPEC.md §4.1
// ---------------------------------------------------------------------------

/**
 * Which pending event a replacement catches. `damage` is the whole damage taxonomy;
 * `lose-lore`, `discard`, `gain-lore` and `ink` are the player-scoped events — "you can't lose
 * lore", "you don't discard", "…would gain lore", "cards enter your inkwell exerted instead".
 * Widening this union is how the next family of "instead" cards joins the pipeline
 * (`replacements.ts` is where the matching lives).
 *
 * `gain-lore` is the mirror of `lose-lore` and covers **every** source of a gain — questing, a
 * location's ◊, an effect's "gain N lore" — because they all pass through one chokepoint
 * (`gainLoreEvent`, `damage.ts`).
 *
 * `ink` is the ordinary once-per-turn ink action (CR §4.2) — a card entering an inkwell always
 * arrives ready unless a replacement says otherwise, e.g. Daisy Duck - Paranormal Investigator's
 * "while this character is exerted, cards enter opponents' inkwells exerted." It shares one
 * chokepoint (`inkEvent`, `damage.ts`); effect-driven puts (`putIntoInkwell`, `zones.ts`) still
 * set their own exertion directly and do not (yet) pass through here.
 *
 * `banish` is card-scoped like `damage` (an affected card, no player), and carries no verbs —
 * "if this character **would be banished**, [something] instead" (ticket 137, Mickey Mouse &
 * Minnie Mouse - Adventuring Duo). It shares one chokepoint (`banishEvent`, `damage.ts`, wrapping
 * `zones.ts`'s `banish`), the same one tickets 68 and 104 already snapshot pre-banish state at.
 */
export type ReplaceableEventKind = 'damage' | 'lose-lore' | 'discard' | 'gain-lore' | 'ink' | 'banish';

/**
 * When a replacement effect applies — "**whenever** one of your other characters **would be**
 * dealt damage" (CR §6.5.3: the condition is checked at the moment the event would occur).
 *
 * Deliberately shaped like `TriggerCondition`. Every replacement's "at most once" is **per
 * event** unconditionally (CR §6.5.6) — the pipeline enforces that via `applied`, no field
 * needed. `limit` is a *further*, optional restriction layered on top: "the first time [this
 * event] would happen, [it doesn't] — **each turn**" (e.g. Lilo - Bundled Up's EXTRA LAYERS),
 * tracked the same way a trigger's own `limit` is (CR §6.1.13.2-shaped, `abilityUses`, cleared
 * in the Ready step). Pair it with `window: 'opponent-turn'` for "the first time, each
 * opponent's turn": the Ready-step reset already happens once per turn regardless of whose, so
 * `window` gating the match is what scopes the limit to a specific seat's turns — including
 * telling two different opponents' turns apart in a multiplayer rotation — without a bespoke
 * per-opponent counter.
 */
export interface ReplacementCondition {
  readonly event: ReplaceableEventKind;
  /** Which damage verbs it catches. `damage` only. Absent means all four — usually too broad; be explicit. */
  readonly verbs?: readonly DamageVerb[];
  /**
   * Whose card the event must be about — the same four scopes a trigger uses, read against
   * the event's affected card. `self` is what makes a replacement a **self-replacement**,
   * which the pipeline applies before anyone else's (CR §6.5.7).
   *
   * A player-scoped event (`lose-lore`, `discard`, `gain-lore`, `ink`) has an affected *player*
   * instead: `controller`/`opponent`/`any` read against that player, and `self` can never
   * match — `defineAbilities` rejects it. `banish` is card-scoped like `damage`, so it reads
   * this exactly the same way.
   */
  readonly subject: 'self' | 'controller' | 'opponent' | 'any';
  /** A further predicate on the affected card ("one of your *other* characters"). `damage`/`banish` only. */
  readonly filter?: Selector;
  /** Whose turn it must be. Default `any-turn`. */
  readonly window?: 'your-turn' | 'opponent-turn' | 'any-turn';
  /** "The first time [event] each turn" — counted per registered ability instance, applied not merely matched. */
  readonly limit?: 'once-per-turn';
  /** A state predicate checked at the moment the event would occur. */
  readonly guard?: Condition;
}

/**
 * A quantity inside a replacement: any ordinary `Amount`, plus **the caught event's own
 * amount** — "put *that many* damage counters on this character instead". `plus` offsets it
 * (a negative `plus` is a reduction), floored at zero.
 */
export type EventAmount = Amount | { readonly kind: 'event-amount'; readonly plus?: number };

/**
 * One event a replacement produces in place of the one it caught. Each produced event
 * **re-enters the pipeline**, so a replacement that another replacement is watching for
 * chains correctly.
 *
 * A `damage` template names cards with ordinary `CardRef`s: `self` is the replacement's own
 * card and `subject` is the caught event's affected card (a player-scoped event has no
 * affected card, so its templates cannot name `subject`). There is no `target` ref, because a
 * replacement resolves without asking anybody anything — `defineAbilities` rejects one.
 *
 * An `ink` template is not a *different* event, unlike every `damage` one — an ink event
 * replaced by another ink event is the same card entering the same inkwell, just with
 * `exerted` overridden. There is no card to name: the caught `InkPendingEvent`'s own `card`
 * and `player` carry straight through (`rewriteEvent`, `replacements.ts`).
 *
 * A `to-inkwell` template is only legal on a `banish` replacement — "put them into your inkwell
 * … instead" (Mickey Mouse & Minnie Mouse - Adventuring Duo). It is not a re-raised `banish`
 * (there is nothing left to banish), and deliberately not an `ink` event either: an effect-driven
 * put into the inkwell does not pass through the `ink` replaceable kind any more than
 * `putIntoInkwell` (`zones.ts`) does, so a "cards enter opponents' inkwells exerted" replacement
 * cannot see it. Like `ink`, there is no card to name — the caught `BanishPendingEvent`'s own
 * `target` carries straight through, into the replacement's own controller's inkwell ("your").
 */
export type EventTemplate =
  | {
      readonly kind: 'damage';
      readonly verb: DamageVerb;
      /** The card the counters land on — or come off, for `remove`. */
      readonly target: CardRef;
      /** Where a `move` takes its counters from. Required for `move`, ignored otherwise. */
      readonly from?: CardRef;
      readonly amount: EventAmount;
    }
  | {
      readonly kind: 'ink';
      /** Whether the card enters exerted instead of ready. Only legal on an `ink` replacement. */
      readonly exerted: boolean;
    }
  | {
      readonly kind: 'to-inkwell';
      /** Whether the card enters exerted. Only legal on a `banish` replacement. Default `true`. */
      readonly exerted?: boolean;
      /** Whether the card enters facedown — knowledge is not granted. Default `false`. */
      readonly facedown?: boolean;
    };

/**
 * A replacement effect: "[event] would happen — [something else] happens **instead**"
 * (CR §6.5).
 *
 * The replaced event **never happens**, so its would-have-been triggers never fire; what the
 * replacement produces does happen, and fires its own. An empty `into` is the complete
 * replacement — prevention, the event simply does not occur.
 *
 * Replacements are read on demand from the cards in play, exactly like continuous statics —
 * there is no registration step and nothing in `GameState` to keep in sync.
 */
export interface ReplacementAbility {
  readonly kind: 'replacement';
  readonly name?: string;
  readonly when: ReplacementCondition;
  /** What happens instead. **Empty means the event is prevented outright.** */
  readonly into: readonly EventTemplate[];
  /**
   * "**The next time** [this happens], [replace it]" (CR-less shorthand, e.g. Rapunzel - Ready
   * for Adventure's ACT OF KINDNESS) — a self-consuming replacement that expires the instant it
   * first *applies*, rather than continuing to match for the rest of whatever duration granted
   * it. Distinct from an ordinary continuous replacement (the default, `undefined`/`false`),
   * which keeps matching every event until its duration or `guard` says otherwise.
   *
   * Only meaningful on a replacement handed out by a resolved `grant-ability` effect
   * (`modifiers.ts`'s `Modifier['kind'] === 'ability'` record is what gets crossed off,
   * per-instance, in `replacements.ts`): a continuous static's `grant-ability` re-offers the
   * ability fresh on every read, with no record to mark, so this flag has nothing to consume
   * there and the grant simply keeps applying. An *authored* (printed) replacement carrying
   * this flag is likewise inert — nothing ever marks it spent — so `consumesOnApply` only does
   * something once the ability is granted.
   */
  readonly consumesOnApply?: boolean;
}

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

export interface KeywordAbility {
  readonly kind: 'keyword';
  readonly keyword: KeywordName;
  /** Challenger +2, Resist +1, Shift 5, Singer 6, Boost 2 … */
  readonly value?: number;
  /** The printed variant name for the Shift family, e.g. `Puppy Shift`, `Universal Shift`. */
  readonly variant?: string;
}

export interface TriggeredAbility {
  readonly kind: 'triggered';
  /** The small-caps name printed on the card, e.g. `CEASELESS WORKER`. */
  readonly name?: string;
  readonly when: TriggerCondition;
  /** Steps run in written order (CR §6.7.2). An array is an implicit `seq`. */
  readonly effect: readonly Effect[];
}

export interface ActivatedAbility {
  readonly kind: 'activated';
  readonly name?: string;
  readonly costs: readonly ActivationCost[];
  readonly effect: readonly Effect[];
  /** "Once during your turn" (CR §6.1.13.2), tracked per ability instance. */
  readonly limit?: 'once-per-turn';
  /**
   * Whose turn, and — for `opponent-turn` — who may activate it at all. Mirrors
   * `TriggeredAbility.window` exactly (`bag.ts`'s `checkWindow`), plus the same third value:
   *
   * - `your-turn` (default) — only the controller, only on the controller's own turn. The
   *   ordinary case; every activated ability before ticket 133 was implicitly this.
   * - `any-turn` — only the controller, on either player's turn. (No printed card needs this
   *   yet — the engine only ever offers turn actions to the active player, so today it behaves
   *   identically to `your-turn`; it exists for the instant-speed activations CR §4.4 allows in
   *   principle.)
   * - `opponent-turn` — "During the opposing player's turn, they may …" (Hercules - Manipulated
   *   Hero's EVERYTHING HAS A PRICE, ticket 133): the ability belongs to whoever is **not** the
   *   controller, and only on that player's own turn — the controller can never activate it, on
   *   any turn. `legalActivations` (`interpret.ts`) scans every other player's play zone for
   *   exactly this window, so the option surfaces to the non-controller like any of their own.
   *   A cost's payer (see `ActivationCost`'s `pay-lore`) follows the activator, not the card's
   *   controller, for the same reason.
   */
  readonly window?: 'your-turn' | 'any-turn' | 'opponent-turn';
  /**
   * "While you have 3 or more characters here, you may [cost] to [effect]" — a live condition
   * the **activation itself** hangs on, evaluated by `canActivate` *before any cost is paid*,
   * the same shape as a static's `while` (CR §6.4.4) but gating legality rather than
   * functioning. It cannot be a check inside the effect: a cost like `banish-self` on a
   * location clears every character's `location` field before the effect runs, so a
   * "characters here" condition read mid-resolution could never observe the board it gated on.
   */
  readonly while?: Condition;
}

export interface StaticAbility {
  readonly kind: 'static';
  readonly name?: string;
  readonly effect: StaticEffect;
  /**
   * "While this character is exerted, …" — a condition the ability's own functioning hangs
   * on (CR §6.4.4). Evaluated on every characteristic read, never stored, so it flips the
   * whole ability off and on as the board changes.
   */
  readonly while?: Condition;
}

/**
 * An **Action** card's effect (CR §5.4). Strictly speaking actions have *effects*, not
 * abilities, and the difference is load-bearing: an action's effect resolves immediately and
 * in full when the card is played, and **never enters the bag** (CR §5.4.1.2, §6.7.4). The
 * card sits in the Play zone while it resolves and is discarded afterwards.
 */
export interface ActionAbility {
  readonly kind: 'action';
  readonly name?: string;
  readonly effect: readonly Effect[];
}

/** One ability of one card. A card's IR is an ordered list of these. */
export type Ability =
  | KeywordAbility
  | TriggeredAbility
  | ActivatedAbility
  | StaticAbility
  | ActionAbility
  | ReplacementAbility;

export type AbilityKind = Ability['kind'];

/**
 * Hand-authored ability IR by LorcanaJSON id — what `packages/cards` publishes and what a
 * `CardCatalog` serves to the engine.
 */
export interface AbilityRegistry {
  get(defId: number): readonly Ability[] | undefined;
}

// ---------------------------------------------------------------------------
// Cloning
// ---------------------------------------------------------------------------

/**
 * Deep-copy an IR value.
 *
 * The IR is plain JSON data by construction, so one recursive walk is correct for every
 * node kind — and, unlike an exhaustive switch, it cannot silently drop a field when the
 * union grows. `clone()` uses it wherever IR rides inside `GameState`.
 */
export function cloneIr<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((each: unknown) => cloneIr(each)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const copy: Record<string, unknown> = {};
    for (const [key, each] of Object.entries(value as Record<string, unknown>)) {
      copy[key] = cloneIr(each);
    }
    return copy as T;
  }
  return value;
}
